import PageHeader from "../components/ds/PageHeader";
import SegmentedControl from "../components/ds/SegmentedControl";
import Switch from "../components/ds/Switch";
import { STATUS_KEYS, STATUS_TONES } from "../components/discover/campaignDisplay";
import Alert from "../components/ds/Alert";
import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import InlineMessage from "../components/ds/InlineMessage";
import Modal from "../components/ds/Modal";
import NavDropdown from "../components/ds/NavDropdown";
import PopUpButton, { POP_UP_CLASS } from "../components/ds/PopUpButton";
import SearchField from "../components/ds/SearchField";
import StatCell from "../components/ds/StatCell";
import TextField from "../components/ds/TextField";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserProfileLink from "../components/UserProfileLink";
import ProductAppsFolder from "../components/ProductAppsFolder";
import { productFolderApps } from "../utils/mintSpaceApps";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiList,
  FiLink,
  FiLoader,
} from "react-icons/fi";
import {
  updateMintSpace,
  getMintSpaceById,
  getMintSpaceActivity,
  getMintSpaceReport,
  getMintSpaceSales,
  getMintSpaceBalance,
  transferMintSpaceToPersonal,
  transferPersonalToMintSpace,
  deleteAppPricing,
  getMintSpaceMembers,
  assignTeamRole,
  getMintSpaceDeletionCheck,
  deleteMintSpace,
} from "../api/mintSpace";
import { uploadMintSpacePolicyFile } from "../api/upload";
import { getBalance, getBalanceAnalytics } from "../api/balance";
import AnalyticsGlossaryModal from "../components/AnalyticsGlossaryModal";
import BusinessAnalyticsDashboard from "../components/BusinessAnalyticsDashboard";
import { getMyCampaigns } from "../api/campaigns";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";

const SECTIONS = Object.freeze({
  HOME: "home",
  CARDS: "cards",
  ANALYTICS: "analytics",
  PRODUCTS: "products",
  SALES: "sales",
  PAYMENTS: "payments",
  BUSINESS: "business", // sidebar-only header — no panel of its own
  REPORTS_OVERVIEW: "reports.overview",
  REPORTS_REVENUE: "reports.revenue",
  REPORTS_SPEND: "reports.spend",
  REPORTS_SUBSCRIPTIONS: "reports.subscriptions",
  REPORTS_REFUNDS: "reports.refunds",
  NOTIFICATIONS: "notifications",
  FEATURES: "features",
  POLICIES: "policies",
  OPTIONS: "options",
  TAX: "tax",
  CONTACT: "contact",
  TEAM: "team",
  DANGER: "danger",
});

const sectionFromUrl = (value) => (Object.values(SECTIONS).includes(value) ? value : null);

/** A sale (or a buyer's purchases) to open the Sales list on — from a sale notification. */
const salesFocusFromUrl = (params) =>
  params.get("section") === SECTIONS.SALES
    ? { purchase: params.get("purchase") || null, buyer: params.get("buyer") || null }
    : null;

/**
 * The section each space's dashboard was last on, for this visit. The Mint Space window
 * starts this page fresh each time its tab is opened, which would otherwise drop it back
 * to Home.
 */
const lastSectionBySpace = new Map();

const REPORT_SECTIONS = new Set([
  SECTIONS.REPORTS_OVERVIEW,
  SECTIONS.REPORTS_REVENUE,
  SECTIONS.REPORTS_SPEND,
  SECTIONS.REPORTS_SUBSCRIPTIONS,
  SECTIONS.REPORTS_REFUNDS,
]);

const REPORT_TYPE_BY_SECTION = {
  [SECTIONS.REPORTS_OVERVIEW]: "overview",
  [SECTIONS.REPORTS_REVENUE]: "revenue",
  [SECTIONS.REPORTS_SPEND]: "spend",
  [SECTIONS.REPORTS_SUBSCRIPTIONS]: "subscriptions",
  [SECTIONS.REPORTS_REFUNDS]: "refunds",
};

/** Georgian small-entrepreneur / LLC codes are numeric strings of 9 or 11 digits. */
const isPlausibleVatId = (v) => /^\d{9,11}$/.test(String(v || "").trim());

/** The Dashboard's pages, for its menu on a computer and its picker on phones and tablets. */
const NAV_ADMIN = {
  label: "navAdmin",
  items: [
    { section: SECTIONS.HOME, icon: "home", label: "navHome" },
    { section: SECTIONS.ANALYTICS, icon: "chart", label: "navAnalytics" },
    { section: SECTIONS.PRODUCTS, icon: "bag", label: "navProducts" },
    { section: SECTIONS.SALES, icon: "bag", label: "navSales" },
    { section: SECTIONS.PAYMENTS, icon: "wallet", label: "navPayments" },
    { section: SECTIONS.CARDS, icon: "wallet", label: "navCards" },
  ],
};

const NAV_REPORTS = {
  label: "navReports",
  items: [
    { section: SECTIONS.REPORTS_OVERVIEW, icon: "chart", label: "reportOverview" },
    { section: SECTIONS.REPORTS_REVENUE, icon: "chart", label: "reportRevenue" },
    { section: SECTIONS.REPORTS_SPEND, icon: "users", label: "reportSpend" },
    { section: SECTIONS.REPORTS_SUBSCRIPTIONS, icon: "calendar", label: "reportSubscriptions" },
    { section: SECTIONS.REPORTS_REFUNDS, icon: "wallet", label: "reportRefunds" },
  ],
};

const NAV_SETTINGS = {
  label: "navSettingsGroup",
  items: [
    { section: SECTIONS.NOTIFICATIONS, icon: "bell", label: "navNotifications" },
    { section: SECTIONS.FEATURES, icon: "filter", label: "navFeatures" },
    { section: SECTIONS.POLICIES, icon: "book", label: "navPolicies" },
    { section: SECTIONS.OPTIONS, icon: "settings", label: "navOptions" },
    { section: SECTIONS.TAX, icon: "filter", label: "navTax" },
    { section: SECTIONS.CONTACT, icon: "message", label: "navContact" },
    { section: SECTIONS.TEAM, icon: "users", label: "navTeam" },
    { section: SECTIONS.DANGER, icon: "warning", label: "navDanger" },
  ],
};

const openCampaigns = (campaigns) => (campaigns || []).filter((c) => c.status === "active" || c.status === "pending").slice(0, 6);

/** Figma's spacing: 24 between blocks on a computer, 20 on a tablet, 16 on a phone. */
const PANEL = "flex flex-col gap-space-16 min-[600px]:gap-space-20 lg:gap-space-24";
const CARD = "rounded-radius-lg bg-bg-raised";
const TWO_UP = "grid grid-cols-1 items-start gap-space-16 min-[600px]:grid-cols-2 min-[600px]:gap-space-20 lg:gap-space-24";
const FOUR_STATS = "grid grid-cols-2 gap-space-16 min-[600px]:gap-space-20 lg:gap-space-24 xl:grid-cols-4";

const formatMoney = (n) =>
  `${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₾`;

const activityLabel = (row, t) => {
  const meta = row.metadata || {};
  switch (row.action) {
    case "campaign.created":
      return t("mintSpaceSettings.activityCampaignCreated", { name: meta.campaignName || "—" });
    case "campaign.updated":
      return t("mintSpaceSettings.activityCampaignUpdated", { name: meta.campaignName || "—" });
    case "campaign.stopped":
      return t("mintSpaceSettings.activityCampaignStopped", { name: meta.campaignName || "—" });
    case "campaign.funded":
      return t("mintSpaceSettings.activityCampaignFunded", {
        name: meta.campaignName || "—",
        amount: (meta.amount ?? 0).toFixed(2),
      });
    case "balance.transfer_from_personal":
      return t("mintSpaceSettings.activityTransferFromPersonal", { amount: (meta.amount ?? 0).toFixed(2) });
    case "balance.transfer_to_personal":
      return t("mintSpaceSettings.activityTransferToPersonal", { amount: (meta.amount ?? 0).toFixed(2) });
    case "settings.updated":
      return t("mintSpaceSettings.activitySettingsUpdated", {
        sections: (meta.sections || []).join(", "),
      });
    case "member.joined":
      return t("mintSpaceSettings.activityMemberJoined");
    case "member.left":
      return t("mintSpaceSettings.activityMemberLeft");
    case "moderator.added":
      return t("mintSpaceSettings.activityModeratorAdded");
    case "moderator.removed":
      return t("mintSpaceSettings.activityModeratorRemoved");
    case "team.role_assigned":
      return t("mintSpaceSettings.activityRoleAssigned", {
        role: t(`mintSpaceSettings.teamRole_${meta.role}`) || meta.role || "—",
      });
    default:
      return row.action;
  }
};

/**
 * `fullHeight`: the page fills its window to the top edge, as in the space, so the menu
 * runs the window's height and the content starts under the floating top bar.
 */
const MintSpaceSettingsPage = ({ isDark, setToast, mintSpace, onUpdate, fullHeight = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const spaceKey = String(mintSpace?._id || "");
  const [activeSection, setActiveSectionState] = useState(
    () =>
      sectionFromUrl(searchParams.get("section")) || lastSectionBySpace.get(spaceKey) || SECTIONS.HOME
  );
  const setActiveSection = useCallback(
    (section) => {
      lastSectionBySpace.set(spaceKey, section);
      setActiveSectionState(section);
    },
    [spaceKey]
  );
  const [salesFocus, setSalesFocus] = useState(() => salesFocusFromUrl(searchParams));
  // ?section=<id> opens that section — e.g. "Delete Mint Space" in the space's menu
  // lands on the danger zone; a sale notification, on that sale. Taken out of the URL once read.
  useEffect(() => {
    if (!searchParams.has("section")) return;
    const section = sectionFromUrl(searchParams.get("section"));
    if (section) setActiveSection(section);
    if (section === SECTIONS.SALES) setSalesFocus(salesFocusFromUrl(searchParams));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("section");
        next.delete("purchase");
        next.delete("buyer");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, setActiveSection]);
  const [saving, setSaving] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [balance, setBalance] = useState({ amount: 0, holdBalance: 0 });
  const [personalBalance, setPersonalBalance] = useState({ amount: 0, holdBalance: 0 });
  const [transferModal, setTransferModal] = useState(null);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [reportRange, setReportRange] = useState(() => defaultRange(30));
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRange, setAnalyticsRange] = useState(() => defaultRange(30));
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [glossaryHighlight, setGlossaryHighlight] = useState(null);

  const openGlossary = (slug) => {
    setGlossaryHighlight(slug || null);
    setGlossaryOpen(true);
  };

  const closeGlossary = () => {
    setGlossaryOpen(false);
    setGlossaryHighlight(null);
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem("mintSpaceDashboard.sidebarCollapsed") === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem("mintSpaceDashboard.sidebarCollapsed", sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  const [form, setForm] = useState(() => defaultFormFromSpace(mintSpace));
  useEffect(() => setForm(defaultFormFromSpace(mintSpace)), [mintSpace]);

  const userId = user?.id ?? user?._id;

  useEffect(() => {
    if (!mintSpace?._id) return;
    getMyCampaigns("", mintSpace._id, true)
      .then((res) => setCampaigns(res?.data || []))
      .catch(() => setCampaigns([]));
  }, [mintSpace?._id]);

  useEffect(() => {
    if (!mintSpace?._id) return;
    getMintSpaceBalance(mintSpace._id)
      .then(setBalance)
      .catch(() => setBalance({ amount: 0, holdBalance: 0 }));
  }, [mintSpace?._id]);

  useEffect(() => {
    if (!userId) return;
    getBalance(userId).then(setPersonalBalance).catch(() => {});
    getBalanceAnalytics(userId).then(setAnalytics).catch(() => setAnalytics(null));
  }, [userId]);

  const refreshBusinessBalance = useCallback(async () => {
    if (!mintSpace?._id) return;
    try {
      const b = await getMintSpaceBalance(mintSpace._id);
      setBalance(b);
    } catch {
      /* ignore */
    }
  }, [mintSpace?._id]);

  const handleTransfer = useCallback(
    async (direction, amount) => {
      if (!mintSpace?._id) return;
      setTransferSubmitting(true);
      try {
        const fn =
          direction === "deposit" ? transferPersonalToMintSpace : transferMintSpaceToPersonal;
        const res = await fn(mintSpace._id, amount);
        if (res?.success !== false) {
          setToast(res?.message || t("toasts.saved"));
          setBalance({
            amount: Number(res?.data?.businessBalance?.amount) || 0,
            holdBalance: balance.holdBalance,
          });
          if (userId) {
            const pb = await getBalance(userId);
            setPersonalBalance(pb);
          }
          setTransferModal(null);
          window.dispatchEvent(new CustomEvent("balance-updated"));
        } else {
          setToast(res?.message || t("toasts.error"));
        }
      } catch (err) {
        setToast(err?.message || t("toasts.error"));
      } finally {
        setTransferSubmitting(false);
      }
    },
    [mintSpace?._id, userId, balance.holdBalance, setToast, t]
  );

  useEffect(() => {
    if (activeSection !== SECTIONS.TEAM || !mintSpace?._id) return;
    setActivityLoading(true);
    getMintSpaceActivity(mintSpace._id, 100)
      .then((res) => setActivity(res?.data || []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [activeSection, mintSpace?._id]);

  const reportType = REPORT_TYPE_BY_SECTION[activeSection];
  useEffect(() => {
    if (!reportType || !mintSpace?._id) return;
    let alive = true;
    // Blow away the previous report's data before the new one arrives — otherwise the
    // report panel briefly tries to render `data` shaped for a different section.
    setReportData(null);
    setReportLoading(true);
    getMintSpaceReport(mintSpace._id, {
      type: reportType,
      from: reportRange.from,
      to: reportRange.to,
    })
      .then((res) => {
        if (!alive) return;
        setReportData(res?.data || null);
      })
      .catch(() => alive && setReportData(null))
      .finally(() => alive && setReportLoading(false));
    return () => {
      alive = false;
    };
  }, [reportType, reportRange.from, reportRange.to, mintSpace?._id]);

  useEffect(() => {
    if (activeSection !== SECTIONS.ANALYTICS || !mintSpace?._id) return;
    let alive = true;
    setAnalyticsData(null);
    setAnalyticsLoading(true);
    getMintSpaceReport(mintSpace._id, {
      type: "dashboard",
      from: analyticsRange.from,
      to: analyticsRange.to,
    })
      .then((res) => alive && setAnalyticsData(res?.data || null))
      .catch(() => alive && setAnalyticsData(null))
      .finally(() => alive && setAnalyticsLoading(false));
    return () => {
      alive = false;
    };
  }, [activeSection, mintSpace?._id, analyticsRange.from, analyticsRange.to]);

  const refreshSpace = useCallback(async () => {
    if (!mintSpace?._id || !onUpdate) return;
    const updated = await getMintSpaceById(mintSpace._id);
    if (updated.success) onUpdate(updated.data);
  }, [mintSpace?._id, onUpdate]);

  const patchSpace = useCallback(
    async (patch, successKey) => {
      if (!mintSpace?._id) return;
      setSaving(true);
      try {
        const res = await updateMintSpace(mintSpace._id, patch);
        if (res.success) {
          setToast(t(successKey || "toasts.saved"));
          await refreshSpace();
        }
      } catch (err) {
        setToast(err.message || t("mintSpace.errorUpdatingMintSpace"));
      } finally {
        setSaving(false);
      }
    },
    [mintSpace?._id, refreshSpace, setToast, t]
  );

  if (!mintSpace) {
    return (
      <div className="flex items-center justify-center py-16 text-label-secondary">
        {t("common.loading") || "Loading..."}
      </div>
    );
  }

  return (
    <div className="text-label-primary">
      <div className="flex flex-col gap-space-16 lg:flex-row lg:gap-space-24">
        <div
          className={`hidden shrink-0 scrollbar-hide overflow-y-auto transition-[width] duration-300 ease-in-out lg:sticky lg:block lg:self-start ${
            fullHeight ? "lg:top-0 lg:max-h-[100vh]" : "lg:top-4 lg:max-h-[calc(100vh-104px)]"
          } ${sidebarCollapsed ? "lg:w-20" : "lg:w-[248px]"}`}
        >
          <Sidebar
            t={t}
            mintSpace={mintSpace}
            campaigns={campaigns}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            navigate={navigate}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          />
        </div>

        <DashboardPicker
          t={t}
          isDark={isDark}
          campaigns={campaigns}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          navigate={navigate}
        />

        <main className={`min-w-0 flex-1 ${fullHeight ? "lg:pt-[104px]" : ""}`}>
          <div className="pb-space-24 min-[600px]:px-space-4 lg:px-0">
            {activeSection === SECTIONS.HOME && (
              <HomePanel
                t={t}
                balance={balance}
                analytics={analytics}
                campaigns={campaigns}
                mintSpace={mintSpace}
                onDeposit={() => setTransferModal("deposit")}
                onWithdraw={() => setTransferModal("withdraw")}
                onCampaignClick={(id) => navigate(`/campaigns/${id}`)}
              />
            )}
            {activeSection === SECTIONS.CARDS && (
              <CardsPanel t={t} onAdd={() => navigate("/balance")} navigate={navigate} />
            )}
            {activeSection === SECTIONS.ANALYTICS && (
              <AnalyticsPanel
                t={t}
                data={analyticsData}
                loading={analyticsLoading}
                range={analyticsRange}
                setRange={setAnalyticsRange}
                onOpenGlossary={openGlossary}
              />
            )}
            {activeSection === SECTIONS.PRODUCTS && (
              <ProductsPanel
                t={t}
                mintSpace={mintSpace}
                navigate={navigate}
                setToast={setToast}
                onRefresh={refreshSpace}
              />
            )}
            {activeSection === SECTIONS.SALES && (
              <SalesPanel t={t} mintSpaceId={mintSpace._id} focus={salesFocus} />
            )}
            {activeSection === SECTIONS.PAYMENTS && (
              <PaymentsPanel
                t={t}
                balance={balance}
                onDeposit={() => setTransferModal("deposit")}
                onWithdraw={() => setTransferModal("withdraw")}
                navigate={navigate}
              />
            )}
            {REPORT_SECTIONS.has(activeSection) && (
              <ReportsPanel
                t={t}
                section={activeSection}
                data={reportData}
                loading={reportLoading}
                range={reportRange}
                setRange={setReportRange}
                onOpenGlossary={openGlossary}
                onOpenSales={() => setActiveSection(SECTIONS.SALES)}
              />
            )}
            {activeSection === SECTIONS.NOTIFICATIONS && (
              <NotificationsPanel
                t={t}
                saving={saving}
                form={form}
                setForm={setForm}
                onSave={(prefs) =>
                  patchSpace(
                    { notificationPrefs: prefs },
                    "mintSpaceSettings.savedNotifications"
                  )
                }
              />
            )}
            {activeSection === SECTIONS.FEATURES && (
              <FeaturesPanel
                t={t}
                saving={saving}
                features={form.features}
                onToggle={(key, val) =>
                  patchSpace(
                    { features: { [key]: val } },
                    val
                      ? "mintSpaceSettings.savedFeatureEnabled"
                      : "mintSpaceSettings.savedFeatureDisabled"
                  )
                }
              />
            )}
            {activeSection === SECTIONS.POLICIES && (
              <PoliciesPanel
                t={t}
                saving={saving}
                setToast={setToast}
                policies={form.policies}
                requireTermsAcceptance={form.options.requireTermsAcceptance}
                setForm={setForm}
                onSave={(p) => patchSpace({ policies: p }, "mintSpaceSettings.savedPolicies")}
                onToggleTC={(val) =>
                  patchSpace(
                    { options: { requireTermsAcceptance: val } },
                    val
                      ? "mintSpaceSettings.savedTCRequired"
                      : "mintSpaceSettings.savedTCOptional"
                  )
                }
              />
            )}
            {activeSection === SECTIONS.OPTIONS && (
              <OptionsPanel
                t={t}
                saving={saving}
                form={form}
                onSaveVat={(vatId) =>
                  patchSpace({ tax: { vatId } }, "mintSpaceSettings.savedVat")
                }
              />
            )}
            {activeSection === SECTIONS.TAX && (
              <TaxPanel
                t={t}
                saving={saving}
                taxType={form.tax.type}
                onSelect={(type) =>
                  patchSpace({ tax: { type } }, "mintSpaceSettings.savedTaxType")
                }
              />
            )}
            {activeSection === SECTIONS.CONTACT && (
              <ContactPanel
                t={t}
                saving={saving}
                business={form.business}
                setForm={setForm}
                onSave={(b) => patchSpace({ business: b }, "mintSpaceSettings.savedContact")}
              />
            )}
            {activeSection === SECTIONS.DANGER && (
              <DangerZonePanel
                t={t}
                isDark={isDark}
                mintSpace={mintSpace}
                navigate={navigate}
                setToast={setToast}
                walletAmount={balance.amount}
                onMoveMoney={() => setTransferModal("withdraw")}
              />
            )}
            {activeSection === SECTIONS.TEAM && (
              <TeamPanel
                t={t}
                mintSpace={mintSpace}
                activity={activity}
                loading={activityLoading}
                setToast={setToast}
                onRefresh={refreshSpace}
              />
            )}
          </div>
        </main>
      </div>

      {transferModal && (
        <BalanceTransferModal
          t={t}
          isDark={isDark}
          mode={transferModal}
          businessBalance={balance}
          personalBalance={personalBalance}
          submitting={transferSubmitting}
          onClose={() => setTransferModal(null)}
          onSubmit={(amount) => handleTransfer(transferModal, amount)}
        />
      )}

      <AnalyticsGlossaryModal
        isDark={isDark}
        isOpen={glossaryOpen}
        onClose={closeGlossary}
        highlightSlug={glossaryHighlight}
      />
    </div>
  );
};

// =============================================================================
// Sidebar
// =============================================================================

const Sidebar = ({ t, mintSpace, campaigns, activeSection, setActiveSection, navigate, collapsed, onToggleCollapsed }) => {
  const campaignRows = openCampaigns(campaigns);
  const navGroup = (group) => (
    <NavGroup label={t(`mintSpaceSettings.${group.label}`)} collapsed={collapsed}>
      {group.items.map((item) => (
        <NavItem
          key={item.section}
          collapsed={collapsed}
          icon={<Icon name={item.icon} size={20} />}
          label={t(`mintSpaceSettings.${item.label}`)}
          active={activeSection === item.section}
          onClick={() => setActiveSection(item.section)}
        />
      ))}
    </NavGroup>
  );

  return (
    <aside
      className={`h-fit w-full bg-bg-base transition-[padding] duration-300 ease-in-out ${collapsed ? "p-2" : "p-3"}`}
    >
      {/* Space + collapse toggle */}
      <div className={`mb-space-8 flex items-center gap-[10px] py-space-4 ${collapsed ? "flex-col" : ""}`}>
        <Avatar size="m" src={mintSpace?.logo} name={mintSpace?.name} />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-subheadline-semibold text-label-primary">{mintSpace?.name || "—"}</div>
            <div className="truncate text-footnote text-label-secondary">{t("mintSpaceSettings.brandDashboard")}</div>
          </div>
        )}
        <IconButton
          label={t(collapsed ? "mintSpaceSettings.expandSidebar" : "mintSpaceSettings.collapseSidebar")}
          onClick={onToggleCollapsed}
        >
          <Icon name={collapsed ? "chevron-right" : "chevron-left"} size={18} />
        </IconButton>
      </div>

      {navGroup(NAV_ADMIN)}
      {navGroup(NAV_REPORTS)}

      <NavGroup label={t("mintSpaceSettings.navBusiness")} collapsed={collapsed}>
        {!collapsed && campaignRows.length === 0 && (
          <div className="px-space-12 py-space-8 text-footnote text-label-secondary">
            {t("mintSpaceSettings.noCampaignsYet")}
          </div>
        )}
        {campaignRows.map((c) => (
          <CampaignNavItem key={c._id} campaign={c} collapsed={collapsed} onClick={() => navigate(`/campaigns/${c._id}`)} />
        ))}
      </NavGroup>

      {navGroup(NAV_SETTINGS)}
    </aside>
  );
};

const NavGroup = ({ label, children, collapsed }) => (
  <div className="mb-space-8 last:mb-0">
    <div className="relative mb-[2px] h-[18px] overflow-hidden">
      <div
        className={`absolute inset-x-0 top-1/2 transition-all duration-300 ease-in-out ${
          collapsed ? "-translate-y-[150%] opacity-0" : "-translate-y-1/2 opacity-100"
        }`}
        aria-hidden={collapsed}
      >
        <div className="text-caps-group uppercase text-label-secondary">{label}</div>
      </div>
      <div
        className={`absolute inset-x-2 top-1/2 border-t border-separator-default transition-all duration-300 ease-in-out ${
          collapsed ? "-translate-y-1/2 opacity-100" : "translate-y-2 opacity-0"
        }`}
        aria-hidden={!collapsed}
      />
    </div>
    <div className="flex flex-col">{children}</div>
  </div>
);

/**
 * When collapsed we hide the label and instead show a floating tooltip on hover /
 * keyboard focus. The selected pill still marks the current section without its text.
 */
const NavItem = ({ icon, label, active, onClick, collapsed = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={collapsed ? label : undefined}
    aria-label={label}
    aria-current={active ? "page" : undefined}
    className={`group relative flex min-h-[44px] w-full items-center rounded-[22px] text-left text-caps-menu uppercase transition-all duration-300 ease-in-out ${
      collapsed ? "justify-center px-0 py-2" : "gap-space-12 px-space-12 py-[10px]"
    } ${active ? "bg-control-selected text-control-selected-label" : "text-label-primary transition-colors hover:bg-fill-quaternary"}`}
  >
    <span className="grid size-5 shrink-0 place-items-center">{icon}</span>
    <span
      className={`truncate transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap ${
        collapsed ? "max-w-0 flex-[0] opacity-0" : "max-w-full flex-1 opacity-100"
      }`}
      aria-hidden={collapsed}
    >
      {label}
    </span>
    {collapsed && <NavTooltip label={label} />}
  </button>
);

const CampaignNavItem = ({ campaign, collapsed = false, onClick }) => {
  const c = campaign;
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? c.campaignName : undefined}
      aria-label={c.campaignName}
      className={`group relative flex min-h-[44px] w-full items-center rounded-[22px] text-left text-subheadline text-label-primary transition-colors hover:bg-fill-quaternary ${
        collapsed ? "justify-center px-0 py-2" : "gap-[10px] px-space-12 py-[10px]"
      }`}
    >
      <Avatar size="s" src={c.campaignLogo} name={c.campaignName} initials={(c.campaignName || "?")[0]} />
      {!collapsed && <span className="min-w-0 flex-1 truncate">{c.campaignName}</span>}
      {!collapsed && <Icon name="chevron-right" size={16} className="text-label-secondary" />}
      {collapsed && <NavTooltip label={c.campaignName} />}
    </button>
  );
};

/** Floating label bubble shown next to collapsed nav items on hover / focus. */
const NavTooltip = ({ label }) => (
  <span
    role="tooltip"
    className="pointer-events-none absolute left-full top-1/2 z-30 ml-2 -translate-y-1/2 whitespace-nowrap rounded-radius-xs bg-bg-overlay px-2.5 py-1 text-footnote text-label-primary opacity-0 shadow-overlay transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100"
  >
    {label}
  </span>
);

/**
 * Figma "Dashboard picker": the same menu on phones and tablets, where the sidebar doesn't
 * fit. The button reads the page you're on and opens the menu, two columns on a tablet.
 */
const DashboardPicker = ({ t, isDark, campaigns, activeSection, setActiveSection, navigate }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const current = [NAV_ADMIN, NAV_REPORTS, NAV_SETTINGS]
    .flatMap((group) => group.items)
    .find((item) => item.section === activeSection);
  const campaignRows = openCampaigns(campaigns);

  const navGroup = (group) => (
    <PickerGroup label={t(`mintSpaceSettings.${group.label}`)}>
      {group.items.map((item) => (
        <NavItem
          key={item.section}
          icon={<Icon name={item.icon} size={20} />}
          label={t(`mintSpaceSettings.${item.label}`)}
          active={activeSection === item.section}
          onClick={() => setActiveSection(item.section)}
        />
      ))}
    </PickerGroup>
  );

  return (
    <div className="min-[600px]:px-space-4 lg:hidden">
      <div ref={anchorRef}>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={`${POP_UP_CLASS} outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
            open ? "shadow-[inset_0_0_0_1.5px_var(--color-accent-default)]" : ""
          }`}
        >
          <span className="min-w-0 flex-1 truncate text-subheadline-semibold">
            {current ? t(`mintSpaceSettings.${current.label}`) : t("mintSpaceSettings.brandDashboard")}
          </span>
          <Icon name="chevron-down" size={16} />
        </button>
      </div>
      {open && (
        <NavDropdown anchorRef={anchorRef} isDark={isDark} maxHeight={558} onClose={close}>
          <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-space-12">
              {navGroup(NAV_ADMIN)}
              {navGroup(NAV_REPORTS)}
            </div>
            <div className="flex min-w-0 flex-col gap-space-12">
              <PickerGroup label={t("mintSpaceSettings.navBusiness")}>
                {campaignRows.length === 0 && (
                  <div className="px-space-12 py-space-8 text-footnote text-label-secondary">
                    {t("mintSpaceSettings.noCampaignsYet")}
                  </div>
                )}
                {campaignRows.map((c) => (
                  <CampaignNavItem key={c._id} campaign={c} onClick={() => navigate(`/campaigns/${c._id}`)} />
                ))}
              </PickerGroup>
              {navGroup(NAV_SETTINGS)}
            </div>
          </div>
        </NavDropdown>
      )}
    </div>
  );
};

const PickerGroup = ({ label, children }) => (
  <div className="flex min-w-0 flex-col">
    <p className="mb-space-4 text-caps-group uppercase text-label-secondary">{label}</p>
    {children}
  </div>
);

// =============================================================================
// Shared bits
// =============================================================================

/** Figma's Page header. On phones its action drops to a row of its own under it. */
const SectionHeader = ({ title, subtitle, action }) => (
  <>
    <PageHeader
      title={title}
      subtitle={subtitle}
      action={action && <div className="hidden min-[600px]:block">{action}</div>}
    />
    {action && <div className="min-[600px]:hidden">{action}</div>}
  </>
);

const GlossaryButton = ({ t, onClick }) => (
  <Button variant="plain" size="s" icon={<Icon name="info" size={16} />} onClick={() => onClick()}>
    {t("mintSpaceSettings.analyticsGlossaryOpen")}
  </Button>
);

const Toggle = ({ on, disabled, onClick, ariaLabel }) => <Switch on={on} disabled={disabled} onClick={onClick} label={ariaLabel} />;

/** One line of a settings list: Figma's rows, inside a raised card with separators. */
const Row = ({ title, description, children }) => (
  <div className="flex items-center justify-between gap-space-16 border-b border-separator-default p-space-16 last:border-b-0">
    <div className="flex min-w-0 flex-1 flex-col gap-space-4">
      <h3 className="text-subheadline-semibold text-label-primary">{title}</h3>
      {description && <p className="text-footnote text-label-secondary">{description}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

/** The wallet card on Home and Payments. */
const BalanceCard = ({ t, label, balance, onDeposit, onWithdraw }) => (
  <div className={`flex flex-col gap-space-12 ${CARD} p-space-24`}>
    <p className="text-caps-label uppercase text-label-secondary">{label}</p>
    <p className="break-words text-large-title text-label-primary">{formatMoney(balance.amount)}</p>
    <p className="text-footnote text-label-secondary">
      {t("mintSpaceSettings.homeOnHold")}: {formatMoney(balance.holdBalance)}
    </p>
    <div className="flex w-fit flex-col items-center gap-space-8 min-[600px]:flex-row">
      <Button size="m" icon={<Icon name="arrow-left" size={20} />} onClick={onDeposit}>
        {t("mintSpaceSettings.homeDepositFromPersonal")}
      </Button>
      <Button size="m" variant="gray" icon={<Icon name="arrow-right" size={20} />} onClick={onWithdraw}>
        {t("mintSpaceSettings.homeTransferToPersonal")}
      </Button>
    </div>
  </div>
);

/** Cards are linked on the personal wallet: Figma's note on Payments and Cards. */
const PersonalWalletNote = ({ t, navigate }) => (
  <InlineMessage title={t("mintSpaceSettings.cardsPersonalWalletNote")}>
    <button type="button" onClick={() => navigate("/balance")} className="text-accent-text hover:underline">
      {t("mintSpaceSettings.cardsPersonalWalletLink")}
    </button>
  </InlineMessage>
);

// =============================================================================
// HOME panel — balance, figures, recent campaigns
// =============================================================================

const HomePanel = ({ t, balance, analytics, campaigns, mintSpace, onDeposit, onWithdraw, onCampaignClick }) => {
  const totalEarned = analytics?.totalEarned ?? 0;
  const activeCampaignCount = campaigns.filter(
    (c) => c.status === "active" || c.status === "pending"
  ).length;

  return (
    <div className={PANEL}>
      <BalanceCard
        t={t}
        label={`${t("mintSpaceSettings.homeTotalBalance")} · ${mintSpace?.name || ""}`}
        balance={balance}
        onDeposit={onDeposit}
        onWithdraw={onWithdraw}
      />

      <div className="grid grid-cols-2 gap-space-16 min-[600px]:grid-cols-3 min-[600px]:gap-space-20 lg:gap-space-24">
        <StatCell label={t("mintSpaceSettings.overviewCampaigns")} value={activeCampaignCount} />
        <StatCell label={t("mintSpaceSettings.overviewMembers")} value={(mintSpace.members || []).length} />
        <StatCell label={t("mintSpaceSettings.homeTotalEarned")} value={formatMoney(totalEarned)} />
      </div>

      <div className={`flex flex-col gap-space-12 ${CARD} p-space-16`}>
        <h3 className="text-headline text-label-primary">{t("mintSpaceSettings.homeRecentCampaigns")}</h3>
        {campaigns.length === 0 ? (
          <p className="text-footnote text-label-tertiary">{t("mintSpaceSettings.noCampaignsYet")}</p>
        ) : (
          <ul className="flex flex-col gap-space-12">
            {campaigns.slice(0, 5).map((c) => (
              <li key={c._id}>
                <button
                  type="button"
                  onClick={() => onCampaignClick?.(c._id)}
                  className="-m-space-4 flex w-[calc(100%+8px)] items-center gap-space-12 rounded-radius-sm p-space-4 text-left transition-colors hover:bg-fill-quaternary"
                >
                  <Avatar size="m" src={c.campaignLogo} name={c.campaignName} initials={(c.campaignName || "?")[0]} />
                  <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                    <p className="truncate text-subheadline-semibold text-label-primary">{c.campaignName}</p>
                    <p className="text-footnote text-label-secondary">
                      {formatMoney(c.budget || 0)} ·{" "}
                      {t("mintSpaceSettings.homeCampaignSpent", { amount: (c.spent || 0).toFixed(2) })}
                    </p>
                  </div>
                  <Badge size="s" tone={STATUS_TONES[c.status] || "warning"}>
                    {t(STATUS_KEYS[c.status] || STATUS_KEYS.pending)}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// CARDS panel
// =============================================================================

/** Real saved cards live on the personal wallet; the business dashboard links there. */
const CardsPanel = ({ t, onAdd, navigate }) => (
  <div className={PANEL}>
    <SectionHeader
      title={t("mintSpaceSettings.cardsTitle")}
      subtitle={t("mintSpaceSettings.cardsSubtitle")}
      action={
        <Button size="m" onClick={onAdd}>
          {t("mintSpaceSettings.cardsAdd")}
        </Button>
      }
    />
    <PersonalWalletNote t={t} navigate={navigate} />
    <div className="flex w-full flex-col gap-space-20 rounded-radius-xl bg-accent-tinted p-space-24 min-[600px]:w-[360px]">
      <p className="text-caps-label uppercase text-accent-text">Mintly</p>
      <p className="text-title-2 text-label-primary">•••• •••• •••• 4242</p>
      <div className="flex items-center justify-between gap-space-8">
        <span className="text-caps-label uppercase text-label-secondary">{t("mintSpaceSettings.cardsCardholder")}</span>
        <span className="text-footnote-semibold text-label-primary">12 / 30</span>
      </div>
    </div>
    <EmptyState
      icon="wallet"
      title={t("mintSpaceSettings.cardsEmptyTitle")}
      message={t("mintSpaceSettings.cardsEmptyBody")}
    />
  </div>
);

// =============================================================================
// REPORTS panels — Overview / Revenue / Spend / Subscriptions / Refunds
// =============================================================================

function defaultRange(days) {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

const RANGE_PRESETS = [
  { key: "7d", days: 7 },
  { key: "30d", days: 30 },
  { key: "90d", days: 90 },
  { key: "365d", days: 365 },
];

const REPORT_TITLES = {
  [SECTIONS.REPORTS_OVERVIEW]: "reportOverview",
  [SECTIONS.REPORTS_REVENUE]: "reportRevenue",
  [SECTIONS.REPORTS_SPEND]: "reportSpend",
  [SECTIONS.REPORTS_SUBSCRIPTIONS]: "reportSubscriptions",
  [SECTIONS.REPORTS_REFUNDS]: "reportRefunds",
};

/** The range on one side, the page's own buttons on the other; stacked on phones. */
const ReportControls = ({ children }) => (
  <div className="flex flex-col items-start gap-space-8 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between">
    {children}
  </div>
);

const ReportsPanel = ({ t, section, data, loading, range, setRange, onOpenGlossary, onOpenSales }) => {
  const titleKey = REPORT_TITLES[section];
  const subscriptions = section === SECTIONS.REPORTS_SUBSCRIPTIONS;

  const handleCsv = () => {
    if (!data) return;
    const rows = csvRowsForSection(section, data, t);
    if (!rows) return;
    downloadCsv(`${titleKey}-${range.from}_to_${range.to}.csv`, rows);
  };

  return (
    <div className={PANEL}>
      <SectionHeader title={t(`mintSpaceSettings.${titleKey}`)} subtitle={t(`mintSpaceSettings.${titleKey}Subtitle`)} />
      {subscriptions ? (
        <div>
          <GlossaryButton t={t} onClick={onOpenGlossary} />
        </div>
      ) : (
        <ReportControls>
          <RangeSwitcher t={t} range={range} setRange={setRange} />
          <div className="flex flex-wrap items-center gap-space-8">
            <GlossaryButton t={t} onClick={onOpenGlossary} />
            <Button variant="gray" size="s" icon={<Icon name="upload" size={16} />} onClick={handleCsv}>
              {t("mintSpaceSettings.reportExport")}
            </Button>
          </div>
        </ReportControls>
      )}

      {loading && !data ? (
        <div className={`${CARD} p-space-24 text-center text-subheadline text-label-secondary`}>
          {t("common.loading") || "Loading..."}
        </div>
      ) : !data ? (
        <EmptyState icon="wallet" title={t("mintSpaceSettings.reportEmpty")} />
      ) : section === SECTIONS.REPORTS_OVERVIEW ? (
        <OverviewReport t={t} data={data} />
      ) : section === SECTIONS.REPORTS_REVENUE ? (
        <RevenueReport t={t} data={data} onOpenSales={onOpenSales} />
      ) : section === SECTIONS.REPORTS_SPEND ? (
        <SpendReport t={t} data={data} />
      ) : subscriptions ? (
        <SubscriptionsReport t={t} data={data} />
      ) : section === SECTIONS.REPORTS_REFUNDS ? (
        <RefundsReport t={t} data={data} />
      ) : null}
    </div>
  );
};

const RangeSwitcher = ({ t, range, setRange }) => {
  const isPreset = (days) => {
    const preset = defaultRange(days);
    return preset.from === range.from && preset.to === range.to;
  };
  const current = RANGE_PRESETS.find((p) => isPreset(p.days))?.key || "";
  return (
    <SegmentedControl
      track="material"
      label={t("mintSpaceSettings.navReports")}
      value={current}
      onChange={(key) => setRange(defaultRange(RANGE_PRESETS.find((p) => p.key === key).days))}
      className="max-w-[280px]"
      options={RANGE_PRESETS.map((p) => ({ value: p.key, label: t(`mintSpaceSettings.range_${p.key}`) }))}
    />
  );
};

// -----------------------------------------------------------------------------
// Overview report
// -----------------------------------------------------------------------------

const OverviewReport = ({ t, data }) => {
  const k = data.kpis || {};
  return (
    <>
      <div className={FOUR_STATS}>
        <StatCell label={t("mintSpaceSettings.kpiRevenueNet")} value={formatMoney(k.revenueNet)} title={t("mintSpaceSettings.tipRevenueNet")} />
        <StatCell label={t("mintSpaceSettings.kpiPayouts")} value={formatMoney(k.spendPaidToCreators)} title={t("mintSpaceSettings.tipPayouts")} />
        <StatCell label={t("mintSpaceSettings.kpiMrr")} value={formatMoney(k.mrr)} title={t("mintSpaceSettings.tipMrr")} />
        <StatCell label={t("mintSpaceSettings.kpiNet")} value={formatMoney(k.net)} title={t("mintSpaceSettings.tipNet")} />
      </div>

      <ChartCard
        title={t("mintSpaceSettings.chartFlow")}
        legend={
          <>
            <LegendDot color="bg-accent-default" label={t("mintSpaceSettings.legendRevenue")} />
            <LegendDot color="bg-label-tertiary" label={t("mintSpaceSettings.legendSpend")} />
          </>
        }
      >
        <DualSeriesChart a={data.revenueSeries} b={data.spendSeries} />
      </ChartCard>

      <div className={TWO_UP}>
        <TopList
          title={t("mintSpaceSettings.topProducts")}
          empty={t("mintSpaceSettings.topProductsEmpty")}
          rows={data.topProducts}
          renderRow={(p) => ({
            key: p._id,
            title: p.name,
            subtitle: `${p.count} ${t("mintSpaceSettings.sales")}`,
            value: formatMoney((p.gross || 0) - (p.refunded || 0)),
          })}
        />
        <TopList
          title={t("mintSpaceSettings.topCampaigns")}
          empty={t("mintSpaceSettings.topCampaignsEmpty")}
          rows={data.topCampaigns}
          renderRow={(c) => ({
            key: c._id,
            title: c.name,
            subtitle: `${c.submissions} ${t("mintSpaceSettings.subs")} · ${(c.views || 0).toLocaleString()} ${t("mintSpaceSettings.views")}`,
            value: formatMoney(c.paidInRange),
          })}
        />
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// Revenue report
// -----------------------------------------------------------------------------

const RevenueReport = ({ t, data, onOpenSales }) => {
  const total = data.totals || {};
  return (
    <>
      <div className={FOUR_STATS}>
        <StatCell label={t("mintSpaceSettings.kpiGross")} value={formatMoney(total.gross)} title={t("mintSpaceSettings.tipGross")} />
        <StatCell label={t("mintSpaceSettings.kpiRefunded")} value={formatMoney(total.refunded)} title={t("mintSpaceSettings.tipRefunded")} />
        <StatCell label={t("mintSpaceSettings.kpiRevenueNet")} value={formatMoney(total.net)} title={t("mintSpaceSettings.tipRevenueNet")} />
        <StatCell label={t("mintSpaceSettings.kpiOrders")} value={(total.count || 0).toLocaleString()} title={t("mintSpaceSettings.tipOrders")} />
      </div>

      <ChartCard title={t("mintSpaceSettings.chartRevenue")}>
        <AreaChart points={data.byDay} />
      </ChartCard>

      <div className={TWO_UP}>
        <TopList
          title={t("mintSpaceSettings.recentPurchases")}
          action={
            onOpenSales && (
              <Button variant="plain" size="s" onClick={onOpenSales}>
                {t("mintSpaceSettings.salesSeeAll")}
              </Button>
            )
          }
          empty={t("mintSpaceSettings.reportEmpty")}
          rows={data.recent}
          limit={data.recent?.length}
          renderRow={(p) => ({
            key: p._id,
            avatar: getProfileAvatarUrl(p.user),
            name: userNameOf(p.user),
            title: p.product,
            subtitle: (
              <>
                <BuyerLink user={p.user} /> · {formatDate(p.paidAt || p.createdAt)}
              </>
            ),
            value: formatMoney(p.amount),
          })}
        />
        <TopList
          title={t("mintSpaceSettings.byProduct")}
          empty={t("mintSpaceSettings.topProductsEmpty")}
          rows={data.byProduct}
          renderRow={(p) => ({
            key: p._id,
            title: p.name || "—",
            subtitle: `${p.count} ${t("mintSpaceSettings.sales")}`,
            value: formatMoney((p.gross || 0) - (p.refunded || 0)),
          })}
        />
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// Spend report (campaign payouts to creators)
// -----------------------------------------------------------------------------

const SpendReport = ({ t, data }) => {
  const total = data.totals || {};
  return (
    <>
      <div className={FOUR_STATS}>
        <StatCell label={t("mintSpaceSettings.kpiPayoutsRange")} value={formatMoney(total.paidToCreators)} />
        <StatCell label={t("mintSpaceSettings.kpiSubmissions")} value={(total.submissions || 0).toLocaleString()} />
        <StatCell label={t("mintSpaceSettings.kpiViews")} value={(total.views || 0).toLocaleString()} />
        <StatCell label={t("mintSpaceSettings.kpiBudgetTotal")} value={formatMoney(total.budgetTotal)} />
      </div>

      <ChartCard title={t("mintSpaceSettings.chartSpend")}>
        <AreaChart points={data.byDay} />
      </ChartCard>

      <div className={TWO_UP}>
        <TopList
          title={t("mintSpaceSettings.byCampaign")}
          empty={t("mintSpaceSettings.topCampaignsEmpty")}
          rows={data.byCampaign}
          renderRow={(c) => ({
            key: c._id,
            title: c.name,
            subtitle: `${c.submissions} ${t("mintSpaceSettings.subs")} · ${(c.views || 0).toLocaleString()} ${t("mintSpaceSettings.views")}`,
            value: formatMoney(c.paidInRange),
          })}
        />
        <TopList
          title={t("mintSpaceSettings.topCreators")}
          empty={t("mintSpaceSettings.topCreatorsEmpty")}
          rows={data.byCreator}
          renderRow={(u) => ({
            key: String(u._id),
            avatar: getProfileAvatarUrl(u.user),
            name: userNameOf(u.user),
            title: <BuyerLink user={u.user} />,
            subtitle: `${u.count} ${t("mintSpaceSettings.subs")} · ${(u.views || 0).toLocaleString()} ${t("mintSpaceSettings.views")}`,
            value: formatMoney(u.total),
          })}
        />
        <TopList
          title={t("mintSpaceSettings.recentPayouts")}
          empty={t("mintSpaceSettings.reportEmpty")}
          rows={data.recent}
          limit={data.recent?.length}
          renderRow={(s) => ({
            key: s._id,
            avatar: getProfileAvatarUrl(s.submittedBy),
            name: userNameOf(s.submittedBy),
            title: <BuyerLink user={s.submittedBy} />,
            subtitle: `${s.campaign?.campaignName || "—"} · ${s.platform} · ${(s.viewCount || 0).toLocaleString()} ${t("mintSpaceSettings.views")} · ${formatDate(s.approvedAt)}`,
            value: formatMoney(s.totalEarned),
          })}
        />
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// Subscriptions report — point-in-time state, no date range
// -----------------------------------------------------------------------------

const SUBSCRIPTION_TONES = { active: "success", past_due: "warning", pending: "warning", revoked: "danger" };

const SubscriptionsReport = ({ t, data }) => {
  const total = data.totals || {};
  return (
    <>
      <div className={FOUR_STATS}>
        <StatCell label={t("mintSpaceSettings.kpiMrr")} value={formatMoney(total.mrr)} title={t("mintSpaceSettings.tipMrr")} />
        <StatCell label={t("mintSpaceSettings.kpiArr")} value={formatMoney(total.arr)} title={t("mintSpaceSettings.tipArr")} />
        <StatCell label={t("mintSpaceSettings.kpiActiveSubs")} value={(total.active || 0).toLocaleString()} title={t("mintSpaceSettings.tipActiveSubs")} />
        <StatCell label={t("mintSpaceSettings.kpiTrialing")} value={(total.trialing || 0).toLocaleString()} title={t("mintSpaceSettings.tipTrialing")} />
      </div>

      <div className={TWO_UP}>
        <div className={`flex min-w-0 flex-col gap-space-12 ${CARD} p-space-16`}>
          <h3 className="text-headline text-label-primary">{t("mintSpaceSettings.byStatus")}</h3>
          {!data.byStatus?.length ? (
            <p className="text-footnote text-label-tertiary">{t("mintSpaceSettings.reportEmpty")}</p>
          ) : (
            <ul className="flex flex-col gap-space-12">
              {data.byStatus.map((s) => (
                <li key={s._id} className="flex items-center justify-between gap-space-8">
                  <Badge size="s" tone={SUBSCRIPTION_TONES[s._id] || "neutral"}>
                    {t(`mintSpaceSettings.subStatus_${s._id}`)}
                  </Badge>
                  <span className="text-subheadline-semibold text-label-primary">{s.count.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <TopList
          title={t("mintSpaceSettings.byProduct")}
          empty={t("mintSpaceSettings.topProductsEmpty")}
          rows={data.byProduct}
          renderRow={(p) => ({
            key: String(p._id),
            title: `#${String(p._id).slice(-6)}`,
            subtitle: `${p.count} ${t("mintSpaceSettings.subscribers")}`,
            value: formatMoney(p.amount),
          })}
        />
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// Refunds report
// -----------------------------------------------------------------------------

const RefundsReport = ({ t, data }) => {
  const total = data.totals || {};
  if (!total.count && !total.refunded) {
    return <EmptyState icon="wallet" title={t("mintSpaceSettings.reportEmpty")} />;
  }
  return (
    <>
      <div className={FOUR_STATS}>
        <StatCell label={t("mintSpaceSettings.kpiRefundedTotal")} value={formatMoney(total.refunded)} title={t("mintSpaceSettings.tipRefunded")} />
        <StatCell label={t("mintSpaceSettings.kpiRefundCount")} value={(total.count || 0).toLocaleString()} title={t("mintSpaceSettings.tipRefundCount")} />
      </div>

      <ChartCard title={t("mintSpaceSettings.chartRefunds")}>
        <AreaChart points={data.byDay} />
      </ChartCard>

      <div className={TWO_UP}>
        <TopList
          title={t("mintSpaceSettings.byProduct")}
          empty={t("mintSpaceSettings.topProductsEmpty")}
          rows={data.byProduct}
          renderRow={(p) => ({
            key: p._id,
            title: p.name || "—",
            subtitle: `${p.count} ${t("mintSpaceSettings.events")}`,
            value: formatMoney(p.refunded),
          })}
        />
        <TopList
          title={t("mintSpaceSettings.recentRefunds")}
          empty={t("mintSpaceSettings.reportEmpty")}
          rows={data.recent}
          limit={data.recent?.length}
          renderRow={(p) => ({
            key: p._id,
            avatar: getProfileAvatarUrl(p.user),
            name: userNameOf(p.user),
            title: p.product,
            subtitle: (
              <>
                <BuyerLink user={p.user} /> · {formatDate(p.updatedAt)}
              </>
            ),
            value: <span className="text-status-danger">−{formatMoney(p.refundedAmount)}</span>,
            note: t(`mintSpaceSettings.purchaseStatus_${p.status}`),
          })}
        />
      </div>
    </>
  );
};

// -----------------------------------------------------------------------------
// Reusable pieces
// -----------------------------------------------------------------------------

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-space-8 text-footnote text-label-secondary">
    <span className={`size-2 rounded-full ${color}`} />
    {label}
  </span>
);

const BuyerLink = ({ user }) => <UserProfileLink user={user} showAvatar={false} fallback={userNameOf(user)} />;

/** An avatar, a title and a line under it, with the figure on the far side. */
const ListRow = ({ avatar, name, title, subtitle, value, note, rowRef, className = "" }) => (
  <li ref={rowRef} className={`flex min-w-0 items-center gap-space-12 ${className}`}>
    <Avatar
      size="m"
      src={avatar}
      name={name || title}
      initials={String((typeof title === "string" ? title : name) || "?")[0].toUpperCase()}
    />
    <div className="flex min-w-0 flex-1 flex-col gap-space-4">
      <div className="truncate text-subheadline-semibold text-label-primary">{title}</div>
      <div className="truncate text-footnote text-label-secondary">{subtitle}</div>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-space-4">
      <div className="text-subheadline-semibold text-label-primary">{value}</div>
      {note && <div className="text-footnote text-label-tertiary">{note}</div>}
    </div>
  </li>
);

const TopList = ({ title, action = null, empty, rows, limit = 8, renderRow }) => (
  <div className={`flex min-w-0 flex-col gap-space-8 ${CARD} p-space-16`}>
    <div className="flex items-center justify-between gap-space-8">
      <h3 className="min-w-0 truncate text-headline text-label-primary">{title}</h3>
      {action}
    </div>
    {!rows?.length ? (
      <p className="text-footnote text-label-tertiary">{empty}</p>
    ) : (
      <ul className="flex flex-col gap-space-12">
        {rows.slice(0, limit).map((row) => {
          const { key, ...cells } = renderRow(row);
          return <ListRow key={key} {...cells} />;
        })}
      </ul>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Charts
// -----------------------------------------------------------------------------

const CHART_GRID = "var(--color-separator-default)";
const CHART_ACCENT = "var(--color-accent-default)";

const hasValues = (...series) => series.some((points) => (points || []).some((p) => p.value));

const ChartCard = ({ title, legend = null, children }) => (
  <div className={`flex min-w-0 flex-col gap-space-12 ${CARD} p-space-16`}>
    <div className="flex flex-wrap items-center justify-between gap-x-space-12 gap-y-space-4">
      <h3 className="min-w-0 text-headline text-label-primary">{title}</h3>
      {legend && <div className="flex flex-wrap items-center gap-space-12">{legend}</div>}
    </div>
    {children}
  </div>
);

/** Figma's chart with nothing to draw: the grid and the line lying on the floor. */
const FlatChart = () => (
  <div className="relative h-[28px]" aria-hidden="true">
    <span className="absolute inset-x-0 top-0 h-px bg-separator-default" />
    <span className="absolute inset-x-0 top-[13px] h-px bg-separator-default" />
    <span className="absolute inset-x-0 bottom-0 h-[2px] bg-accent-default" />
  </div>
);

/** Single-series area chart with tooltip on hover. */
const AreaChart = ({ points }) => {
  const [hover, setHover] = useState(null);
  const width = 900;
  const height = 220;
  const padY = 14;
  // Guard against callers that pass undefined during a section switch.
  const safePoints = Array.isArray(points) ? points : [];
  if (!hasValues(safePoints)) return <FlatChart />;
  const values = safePoints.map((p) => p.value);
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, safePoints.length - 1);
  const xy = safePoints.map((p, i) => [i * step, padY + (height - 2 * padY) * (1 - p.value / max)]);

  const linePath = xy.reduce((acc, [x, y], i, arr) => {
    if (i === 0) return `M ${x} ${y}`;
    const [px, py] = arr[i - 1];
    const midX = (px + x) / 2;
    return `${acc} Q ${px} ${py}, ${midX} ${(py + y) / 2} T ${x} ${y}`;
  }, "");
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rx = ((e.clientX - rect.left) / rect.width) * width;
    const idx = Math.min(safePoints.length - 1, Math.max(0, Math.round(rx / step)));
    setHover(idx);
  };

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-56 w-full"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="dashboard-chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_ACCENT} stopOpacity={0.25} />
            <stop offset="100%" stopColor={CHART_ACCENT} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* subtle horizontal grid */}
        {[0.25, 0.5, 0.75].map((f, i) => (
          <line key={i} x1={0} x2={width} y1={padY + (height - 2 * padY) * f} y2={padY + (height - 2 * padY) * f} stroke={CHART_GRID} />
        ))}
        <path d={areaPath} fill="url(#dashboard-chart-fill)" />
        <path d={linePath} fill="none" stroke={CHART_ACCENT} strokeWidth={2.5} strokeLinecap="round" />
        {hover != null && xy[hover] && (
          <>
            <line x1={xy[hover][0]} x2={xy[hover][0]} y1={padY} y2={height - padY} stroke={CHART_GRID} strokeWidth={1} />
            <circle cx={xy[hover][0]} cy={xy[hover][1]} r={4} fill={CHART_ACCENT} stroke="var(--color-bg-raised)" strokeWidth={2} />
          </>
        )}
      </svg>
      {hover != null && safePoints[hover] && (
        <div
          className="pointer-events-none absolute top-2 rounded-radius-xs bg-bg-overlay px-2 py-1 text-caption-1 shadow-overlay"
          style={{ left: `min(${(xy[hover][0] / width) * 100}%, calc(100% - 130px))` }}
        >
          <div className="font-semibold text-label-primary">{formatMoney(safePoints[hover].value)}</div>
          <div className="text-label-secondary">{safePoints[hover].date}</div>
        </div>
      )}
    </div>
  );
};

/** Two overlapping series (revenue vs spend). */
const DualSeriesChart = ({ a, b }) => {
  if (!hasValues(a, b)) return <FlatChart />;
  const width = 900;
  const height = 220;
  const padY = 14;
  const len = Math.max(a?.length || 0, b?.length || 0);
  const max = Math.max(1, ...(a || []).map((p) => p.value), ...(b || []).map((p) => p.value));
  const step = width / Math.max(1, len - 1);
  const pathOf = (arr) => (arr || []).reduce((acc, p, i) => {
    const x = i * step;
    const y = padY + (height - 2 * padY) * (1 - p.value / max);
    if (i === 0) return `M ${x} ${y}`;
    const prev = arr[i - 1];
    const px = (i - 1) * step;
    const py = padY + (height - 2 * padY) * (1 - prev.value / max);
    const midX = (px + x) / 2;
    return `${acc} Q ${px} ${py}, ${midX} ${(py + y) / 2} T ${x} ${y}`;
  }, "");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="h-56 w-full">
      {[0.25, 0.5, 0.75].map((f, i) => (
        <line key={i} x1={0} x2={width} y1={padY + (height - 2 * padY) * f} y2={padY + (height - 2 * padY) * f} stroke={CHART_GRID} />
      ))}
      <path d={pathOf(a)} fill="none" stroke={CHART_ACCENT} strokeWidth={2.5} strokeLinecap="round" />
      <path d={pathOf(b)} fill="none" stroke="var(--color-label-tertiary)" strokeWidth={2.5} strokeLinecap="round" strokeDasharray="4 4" />
    </svg>
  );
};

// -----------------------------------------------------------------------------
// CSV export
// -----------------------------------------------------------------------------

function csvRowsForSection(section, data, t) {
  if (section === SECTIONS.REPORTS_REVENUE) {
    const header = ["date", "amount"];
    const rows = (data.byDay || []).map((p) => [p.date, p.value.toFixed(2)]);
    return [header, ...rows];
  }
  if (section === SECTIONS.REPORTS_SPEND) {
    const header = ["date", "paid_to_creators"];
    const rows = (data.byDay || []).map((p) => [p.date, p.value.toFixed(2)]);
    return [header, ...rows];
  }
  if (section === SECTIONS.REPORTS_REFUNDS) {
    const header = ["date", "refunded"];
    const rows = (data.byDay || []).map((p) => [p.date, p.value.toFixed(2)]);
    return [header, ...rows];
  }
  if (section === SECTIONS.REPORTS_OVERVIEW) {
    const header = ["date", "revenue", "spend"];
    const map = new Map((data.spendSeries || []).map((p) => [p.date, p.value]));
    const rows = (data.revenueSeries || []).map((p) => [p.date, p.value.toFixed(2), (map.get(p.date) || 0).toFixed(2)]);
    return [header, ...rows];
  }
  return null;
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((r) => r.map((v) => (String(v).includes(",") ? `"${String(v).replace(/"/g, '""')}"` : v)).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function userNameOf(u) {
  if (!u) return "—";
  const name = [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(" ");
  return name || u.username || u.email || "—";
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString();
}

// =============================================================================
// Notifications
// =============================================================================

const NOTIFICATION_COLUMN = "flex w-[80px] shrink-0 justify-center";

const NotificationsPanel = ({ t, saving, form, setForm, onSave }) => {
  const events = ["newSubmission", "appeal", "payout", "supportMessage"];
  const toggle = (channel, evt) => {
    const next = {
      ...form.notificationPrefs,
      [channel]: {
        ...form.notificationPrefs[channel],
        [evt]: !form.notificationPrefs[channel][evt],
      },
    };
    setForm({ ...form, notificationPrefs: next });
    onSave(next);
  };
  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.notificationsTitle")}
        subtitle={t("mintSpaceSettings.notificationsSubtitle")}
      />
      <div className={`overflow-hidden ${CARD}`}>
        <div className="flex items-center bg-fill-quaternary px-space-16 py-space-12 text-caps-label uppercase text-label-secondary">
          <span className="min-w-0 flex-1">{t("mintSpaceSettings.notificationsEvent")}</span>
          <span className={NOTIFICATION_COLUMN}>{t("mintSpaceSettings.notificationsEmail")}</span>
          <span className={NOTIFICATION_COLUMN}>{t("mintSpaceSettings.notificationsInApp")}</span>
        </div>
        {events.map((evt) => (
          <div key={evt} className="flex items-center border-t border-separator-default px-space-16 py-space-12">
            <span className="min-w-0 flex-1 text-subheadline-semibold text-label-primary">
              {t(`mintSpaceSettings.event_${evt}`)}
            </span>
            <span className={NOTIFICATION_COLUMN}>
              <Toggle
                on={!!form.notificationPrefs.email[evt]}
                disabled={saving}
                onClick={() => toggle("email", evt)}
                ariaLabel={`${t("mintSpaceSettings.notificationsEmail")} ${evt}`}
              />
            </span>
            <span className={NOTIFICATION_COLUMN}>
              <Toggle
                on={!!form.notificationPrefs.inApp[evt]}
                disabled={saving}
                onClick={() => toggle("inApp", evt)}
                ariaLabel={`${t("mintSpaceSettings.notificationsInApp")} ${evt}`}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Features
// =============================================================================

const FeaturesPanel = ({ t, saving, features, onToggle }) => (
  <div className={PANEL}>
    <SectionHeader
      title={t("mintSpaceSettings.featuresTitle")}
      subtitle={t("mintSpaceSettings.featuresSubtitle")}
    />
    <div className={`overflow-hidden ${CARD}`}>
      <Row
        title={t("mintSpaceSettings.featurePayments")}
        description={t("mintSpaceSettings.featurePaymentsHelp")}
      >
        <Toggle on={features.payments} disabled={saving} onClick={() => onToggle("payments", !features.payments)} />
      </Row>
      <Row
        title={t("mintSpaceSettings.featureSupportChat")}
        description={t("mintSpaceSettings.featureSupportChatHelp")}
      >
        <Toggle
          on={features.supportChat}
          disabled={saving}
          onClick={() => onToggle("supportChat", !features.supportChat)}
        />
      </Row>
      <Row
        title={t("mintSpaceSettings.featureWithdrawals")}
        description={t("mintSpaceSettings.featureWithdrawalsHelp")}
      >
        <Toggle
          on={features.withdrawals}
          disabled={saving}
          onClick={() => onToggle("withdrawals", !features.withdrawals)}
        />
      </Row>
    </div>
  </div>
);

// =============================================================================
// Policies
// =============================================================================

const PoliciesPanel = ({ t, saving, policies, requireTermsAcceptance, setForm, onSave, onToggleTC, setToast }) => {
  const [draft, setDraft] = useState(policies);
  useEffect(() => setDraft(policies), [policies]);

  const patchDoc = (key, patch) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const saveDoc = (key) => {
    setForm((f) => ({ ...f, policies: draft }));
    onSave({ [key]: draft[key] });
  };

  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.policiesTitle")}
        subtitle={t("mintSpaceSettings.policiesSubtitle")}
      />
      <div className={`overflow-hidden ${CARD}`}>
        <Row
          title={t("mintSpaceSettings.optionRequireTC")}
          description={t("mintSpaceSettings.optionRequireTCHelp")}
        >
          <Toggle
            on={requireTermsAcceptance}
            disabled={saving}
            onClick={() => {
              const next = !requireTermsAcceptance;
              setForm((f) => ({
                ...f,
                options: { ...f.options, requireTermsAcceptance: next },
              }));
              onToggleTC(next);
            }}
          />
        </Row>
      </div>
      <PolicyEditor
        t={t}
        saving={saving}
        setToast={setToast}
        label={t("mintSpaceSettings.policyTerms")}
        kind="terms"
        doc={draft.terms}
        onChange={(patch) => patchDoc("terms", patch)}
        onSave={() => saveDoc("terms")}
      />
      <PolicyEditor
        t={t}
        saving={saving}
        setToast={setToast}
        label={t("mintSpaceSettings.policyPrivacy")}
        kind="privacy"
        doc={draft.privacy}
        onChange={(patch) => patchDoc("privacy", patch)}
        onSave={() => saveDoc("privacy")}
      />
    </div>
  );
};

/**
 * Single-document policy editor. Renders one of three panes based on `doc.mode`:
 *   - "text": rich-text WYSIWYG (bold/italic/underline, alignment, lists, links)
 *   - "file": drag/drop or click-to-upload, plus a shown filename + preview link
 *   - "url":  bare URL input
 * All three modes coexist on the record — switching mode doesn't lose your other content
 * (e.g. an authored HTML draft stays around if the user flips to URL and back).
 */
const PolicyEditor = ({ t, saving, setToast, label, kind, doc, onChange, onSave }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const mode = doc?.mode || "url";

  const onPickFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMintSpacePolicyFile(file, kind);
      if (res?.success && res.url) {
        onChange({ fileUrl: res.url, fileName: res.fileName || file.name });
        setToast?.(t("mintSpaceSettings.policyUploadOk"));
      }
    } catch (err) {
      setToast?.(err?.message || t("mintSpaceSettings.policyUploadErr"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const previewHref =
    mode === "file" ? doc?.fileUrl : mode === "url" ? doc?.url : null;

  return (
    <div className={`flex flex-col gap-space-12 ${CARD} p-space-16`}>
      <div className="flex flex-wrap items-center justify-between gap-space-8">
        <h3 className="text-headline text-label-primary">{label}</h3>
        <div className="flex w-full min-w-0 items-center gap-space-8 min-[600px]:w-auto">
          {previewHref && (
            <a
              href={previewHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-button-s shrink-0 items-center gap-space-4 px-space-12 text-caps-button-s uppercase text-accent-text"
            >
              <Icon name="link" size={16} />
              {t("mintSpaceSettings.policyOpen")}
            </a>
          )}
          <SegmentedControl
            track="material"
            label={label}
            value={mode}
            onChange={(m) => onChange({ mode: m })}
            className="min-w-0 flex-1 min-[600px]:w-[240px] min-[600px]:flex-none [&>button]:px-space-8"
            options={[
              { value: "text", label: t("mintSpaceSettings.policyModeText") },
              { value: "file", label: t("mintSpaceSettings.policyModeFile") },
              { value: "url", label: t("mintSpaceSettings.policyModeUrl") },
            ]}
          />
        </div>
      </div>

      {mode === "text" && (
        <RichTextEditor
          t={t}
          value={doc?.html || ""}
          onChange={(html) => onChange({ html })}
          placeholder={t("mintSpaceSettings.policyTextPlaceholder")}
        />
      )}

      {mode === "file" && (
        <>
          <div
            className="flex flex-col items-center justify-center gap-space-8 rounded-[22px] border border-dashed border-separator-default p-space-24 text-center"
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onPickFile(f);
            }}
          >
            <p className="text-subheadline-semibold text-label-primary">{t("mintSpaceSettings.policyDropOrClick")}</p>
            <p className="text-footnote text-label-secondary">{t("mintSpaceSettings.policyAcceptedFormats")}</p>
            <Button
              variant="gray"
              size="s"
              icon={<Icon name="upload" size={16} />}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? t("mintSpaceSettings.policyUploading") : t("mintSpaceSettings.policyChooseFile")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.md,.html,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/plain,text/markdown,text/html"
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
          </div>

          {doc?.fileUrl && (
            <div className="flex items-center gap-space-12 rounded-[22px] bg-bg-inset p-space-12">
              <div className="min-w-0 flex-1">
                <div className="truncate text-subheadline-semibold text-label-primary">
                  {doc.fileName || t("mintSpaceSettings.policyUploadedFile")}
                </div>
                <a
                  href={doc.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block truncate text-footnote text-accent-text hover:underline"
                >
                  {doc.fileUrl}
                </a>
              </div>
              <IconButton
                label={t("mintSpaceSettings.policyRemoveFile")}
                variant="plain"
                className="text-status-danger"
                onClick={() => onChange({ fileUrl: "", fileName: "" })}
              >
                <Icon name="trash" size={18} />
              </IconButton>
            </div>
          )}

          <TextField
            type="url"
            label={t("mintSpaceSettings.policyLinkExisting")}
            hint={t("mintSpaceSettings.policyLinkExistingHint")}
            icon={<Icon name="link" size={20} />}
            value={doc?.fileUrl || ""}
            onChange={(e) => onChange({ fileUrl: e.target.value })}
            placeholder="https://…"
          />
        </>
      )}

      {mode === "url" && (
        <TextField
          type="url"
          label={t("mintSpaceSettings.policyExternalUrl")}
          hint={t("mintSpaceSettings.policyExternalUrlHint")}
          icon={<Icon name="link" size={20} />}
          value={doc?.url || ""}
          onChange={(e) => onChange({ url: e.target.value })}
          placeholder="https://…"
        />
      )}

      <Button size="m" className="self-start" disabled={saving || uploading} onClick={onSave}>
        {t("common.save") || "Save"}
      </Button>
    </div>
  );
};

/**
 * ContentEditable-based rich-text editor. Uses `document.execCommand` for formatting —
 * technically deprecated but universally supported and dependency-free, which is the right
 * tradeoff for a small in-app policy editor.
 *
 * The editor is *uncontrolled internally* on the DOM side: we only push `value` into the
 * DOM when it changes *externally* (initial load, saved reload), so typing never resets
 * the caret.
 */
const RichTextEditor = ({ t, value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const lastAppliedValue = useRef(value);

  useEffect(() => {
    // Only overwrite DOM if the incoming value differs from what we last emitted.
    // Prevents the browser from re-rendering (and killing the caret) on every keystroke.
    if (editorRef.current && value !== lastAppliedValue.current) {
      editorRef.current.innerHTML = value || "";
      lastAppliedValue.current = value;
    }
  }, [value]);

  const exec = (command, arg = null) => {
    editorRef.current?.focus();
    // execCommand is deprecated but retains near-universal support for the formatting
    // operations we need. Migration to Selection/Range APIs isn't worth it here.
    document.execCommand(command, false, arg);
    emitChange();
  };

  const emitChange = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastAppliedValue.current = html;
    onChange(html);
  };

  const addLink = () => {
    const href = window.prompt(t("mintSpaceSettings.policyLinkPrompt"));
    if (!href) return;
    exec("createLink", href);
  };

  return (
    <div className="overflow-hidden rounded-[22px] bg-fill-tertiary">
      <div className="flex flex-wrap items-center gap-[2px] border-b border-separator-default p-space-8">
        <ToolbarBtn onClick={() => exec("bold")} title={t("mintSpaceSettings.tb_bold")}>
          <FiBold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("italic")} title={t("mintSpaceSettings.tb_italic")}>
          <FiItalic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("underline")} title={t("mintSpaceSettings.tb_underline")}>
          <FiUnderline className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <ToolbarSep />

        <ToolbarBtn onClick={() => exec("justifyLeft")} title={t("mintSpaceSettings.tb_alignLeft")}>
          <FiAlignLeft className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("justifyCenter")} title={t("mintSpaceSettings.tb_alignCenter")}>
          <FiAlignCenter className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("justifyRight")} title={t("mintSpaceSettings.tb_alignRight")}>
          <FiAlignRight className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("justifyFull")} title={t("mintSpaceSettings.tb_alignJustify")}>
          <FiAlignJustify className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <ToolbarSep />

        <ToolbarBtn onClick={() => exec("insertUnorderedList")} title={t("mintSpaceSettings.tb_bullets")}>
          <FiList className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("formatBlock", "H2")} title={t("mintSpaceSettings.tb_heading")}>
          <span className="text-caption-1-semibold">H</span>
        </ToolbarBtn>
        <ToolbarBtn onClick={() => exec("formatBlock", "P")} title={t("mintSpaceSettings.tb_paragraph")}>
          <span className="text-caption-1-semibold">P</span>
        </ToolbarBtn>
        <ToolbarBtn onClick={addLink} title={t("mintSpaceSettings.tb_link")}>
          <FiLink className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <ToolbarSep />

        <ToolbarBtn onClick={() => exec("removeFormat")} title={t("mintSpaceSettings.tb_clear")}>
          <span className="text-caption-1-semibold">Tx</span>
        </ToolbarBtn>
      </div>

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onBlur={emitChange}
          onPaste={(e) => {
            // Strip formatting on paste. Users pasting from Word/Google Docs otherwise
            // drag in fonts, colours and Microsoft-specific classes that don't survive
            // sanitisation on save and look inconsistent while editing.
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
          }}
          className="policy-editor min-h-[220px] max-h-[520px] overflow-y-auto px-space-16 py-space-12 text-callout text-label-primary outline-none"
          data-placeholder={placeholder}
          style={{ lineHeight: 1.55 }}
        />
        <style>{`
          .policy-editor:empty:before {
            content: attr(data-placeholder);
            color: var(--color-label-tertiary);
            pointer-events: none;
          }
          .policy-editor h1, .policy-editor h2, .policy-editor h3 {
            font-weight: 700;
            margin: 0.6em 0 0.3em;
          }
          .policy-editor h2 { font-size: 1.15rem; }
          .policy-editor h3 { font-size: 1.05rem; }
          .policy-editor p { margin: 0.35em 0; }
          .policy-editor ul, .policy-editor ol { padding-left: 1.4em; margin: 0.4em 0; }
          .policy-editor ul { list-style: disc; }
          .policy-editor ol { list-style: decimal; }
          .policy-editor a { color: var(--color-accent-text); text-decoration: underline; }
        `}</style>
      </div>
    </div>
  );
};

const ToolbarBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault() /* keep selection in the editor */}
    onClick={onClick}
    title={title}
    className="inline-flex size-7 items-center justify-center rounded-radius-xs text-label-primary transition-colors hover:bg-fill-secondary"
  >
    {children}
  </button>
);

const ToolbarSep = () => <span className="mx-1 h-5 w-px bg-separator-default" />;

// =============================================================================
// Options
// =============================================================================

const OptionsPanel = ({ t, saving, form, onSaveVat }) => {
  const [vat, setVat] = useState(form.tax.vatId || "");
  useEffect(() => setVat(form.tax.vatId || ""), [form.tax.vatId]);
  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.optionsTitle")}
        subtitle={t("mintSpaceSettings.optionsSubtitle")}
      />
      <div className={`flex items-end gap-space-8 ${CARD} p-space-16`}>
        <TextField
          inputMode="numeric"
          label={t("mintSpaceSettings.optionVatLabel")}
          hint={t("mintSpaceSettings.optionVatHelp")}
          error={vat && !isPlausibleVatId(vat) ? t("mintSpaceSettings.optionVatWarn") : undefined}
          value={vat}
          onChange={(e) => setVat(e.target.value)}
          placeholder="123456789"
          className="min-w-0 flex-1"
        />
        <Button size="m" disabled={saving} onClick={() => onSaveVat(vat.trim())}>
          {t("common.save") || "Save"}
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// Tax
// =============================================================================

const TaxPanel = ({ t, saving, taxType, onSelect }) => (
  <div className={PANEL}>
    <SectionHeader
      title={t("mintSpaceSettings.taxTitle")}
      subtitle={t("mintSpaceSettings.taxSubtitle")}
    />
    <div className={TWO_UP}>
      {["inclusive", "exclusive"].map((type) => {
        const selected = taxType === type;
        return (
          <button
            key={type}
            type="button"
            disabled={saving}
            aria-pressed={selected}
            onClick={() => onSelect(type)}
            className={`flex flex-col gap-space-8 ${CARD} p-space-16 text-left ${
              selected ? "shadow-[inset_0_0_0_1px_var(--color-accent-default)]" : "transition-colors hover:bg-fill-quaternary"
            }`}
          >
            <span className="flex items-center justify-between gap-space-8">
              <span className="min-w-0 truncate text-headline text-label-primary">{t(`mintSpaceSettings.tax_${type}`)}</span>
              <Badge size="s" tone={selected ? "success" : "neutral"}>
                {selected ? t("mintSpaceSettings.taxSelected") : t("mintSpaceSettings.taxSelectableHint")}
              </Badge>
            </span>
            <span className="text-footnote text-label-secondary">{t(`mintSpaceSettings.tax_${type}_help`)}</span>
          </button>
        );
      })}
    </div>
  </div>
);

// =============================================================================
// Contact
// =============================================================================

const ContactPanel = ({ t, saving, business, setForm, onSave }) => {
  const [draft, setDraft] = useState(business);
  useEffect(() => setDraft(business), [business]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const fields = [
    { key: "supportEmail", label: t("mintSpaceSettings.contactSupportEmail"), type: "email" },
    { key: "name", label: t("mintSpaceSettings.contactBusinessName") },
    { key: "streetAddress", label: t("mintSpaceSettings.contactStreet"), full: true },
    { key: "apt", label: `${t("mintSpaceSettings.contactApt")} (${t("mintSpaceSettings.contactOptional")})` },
    { key: "city", label: t("mintSpaceSettings.contactCity") },
    { key: "zipCode", label: t("mintSpaceSettings.contactZip") },
    { key: "country", label: t("mintSpaceSettings.contactCountry"), maxLength: 2 },
  ];

  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.contactTitle")}
        subtitle={t("mintSpaceSettings.contactSubtitle")}
      />
      <div className={`flex flex-col gap-space-16 ${CARD} p-space-16`}>
        <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2">
          {fields.map((f) => (
            <TextField
              key={f.key}
              type={f.type || "text"}
              label={f.label}
              value={draft[f.key] || ""}
              maxLength={f.maxLength}
              onChange={(e) => set(f.key, e.target.value)}
              className={f.full ? "min-[600px]:col-span-2" : ""}
            />
          ))}
        </div>
        <Button
          size="m"
          className="self-start"
          disabled={saving}
          onClick={() => {
            setForm((prev) => ({ ...prev, business: draft }));
            onSave(draft);
          }}
        >
          {t("common.save") || "Save"}
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// Analytics / Products / Payments panels
// =============================================================================

const AnalyticsPanel = ({ t, data, loading, range, setRange, onOpenGlossary }) => (
  <div className="flex flex-col gap-space-16 min-[600px]:gap-space-24">
    <SectionHeader
      title={t("mintSpaceSettings.analyticsTitle")}
      subtitle={t("mintSpaceSettings.analyticsDashboardSubtitle")}
    />
    <ReportControls>
      <RangeSwitcher t={t} range={range} setRange={setRange} />
      <GlossaryButton t={t} onClick={onOpenGlossary} />
    </ReportControls>
    {loading && !data ? (
      <div className={`${CARD} p-space-24 text-center text-subheadline text-label-secondary`}>
        {t("common.loading") || "Loading..."}
      </div>
    ) : !data ? (
      <EmptyState icon="wallet" title={t("mintSpaceSettings.reportEmpty")} />
    ) : (
      <BusinessAnalyticsDashboard t={t} data={data} onOpenGlossary={onOpenGlossary} />
    )}
  </div>
);

const ProductsPanel = ({ t, mintSpace, navigate, setToast, onRefresh }) => {
  const products = mintSpace?.appPricing || [];
  const catalog = useMemo(() => getMintSpaceAppStoreCatalog(t), [t]);
  const [deleting, setDeleting] = useState(null);
  const [removing, setRemoving] = useState(null);

  const formatProductPrice = (p) => {
    if (p.accessType === "free") return t("mintSpace.free") || "Free";
    const sym = p.currency === "GEL" ? "₾" : p.currency || "₾";
    const period =
      p.billingType === "recurring"
        ? ` / ${p.period === "1w" ? "week" : p.period === "1y" ? "year" : "month"}`
        : "";
    return `${sym}${Number(p.price || 0).toFixed(2)}${period}`;
  };

  const handleDelete = async (productId) => {
    setDeleting(productId);
    try {
      const res = await deleteAppPricing(mintSpace._id, productId);
      if (res?.success !== false) {
        setToast(t("mintSpaceSettings.productsDeleted") || "Product deleted");
        await onRefresh?.();
      }
    } catch (err) {
      setToast(err?.message || t("toasts.error"));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.productsTitle")}
        subtitle={t("mintSpaceSettings.productsSubtitle")}
        action={
          <Button size="m" onClick={() => navigate(`/mint-space/${mintSpace._id}?nav=pricing`)}>
            {t("mintSpaceSettings.productsAdd")}
          </Button>
        }
      />
      {!products.length ? (
        <div className={`${CARD} p-space-24 text-center text-subheadline text-label-secondary`}>
          {t("mintSpaceSettings.productsEmpty")}
        </div>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,380px),380px))] gap-space-16 min-[600px]:gap-space-20 lg:gap-space-24">
          {products.map((p) => (
            <li key={p._id} className={`flex min-w-0 flex-col gap-space-16 ${CARD} p-space-16 min-[600px]:items-center`}>
              <div className="flex w-full min-w-0 items-center gap-space-12">
                <Avatar size="l" src={p.listingBanner || p.bannerImage} name={p.name || p.headline} />
                <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                  <p className="truncate text-headline text-label-primary">
                    {p.name || p.headline || t("mintSpaceSettings.productsUntitled")}
                  </p>
                  <p className="truncate text-footnote text-label-secondary">{formatProductPrice(p)}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-space-8 min-[600px]:justify-center">
                <Button
                  size="s"
                  variant="gray"
                  icon={<Icon name="edit" size={16} />}
                  onClick={() => navigate(`/mint-space/${mintSpace._id}?nav=pricing&product=${p._id}`)}
                >
                  {t("common.edit")}
                </Button>
                <a
                  href={`/mint-space/${mintSpace._id}/product/${p._id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-button-s items-center gap-space-4 rounded-radius-md bg-accent-tinted px-space-12 text-caps-button-s uppercase text-accent-tinted-label"
                >
                  <Icon name="link" size={16} />
                  {t("mintSpaceSettings.productsPublicLink")}
                </a>
                <IconButton
                  label={t("common.delete")}
                  variant="plain"
                  className="text-status-danger"
                  disabled={deleting === p._id}
                  onClick={() => setRemoving(p)}
                >
                  <Icon name="trash" size={18} />
                </IconButton>
                <ProductAppsFolder
                  spaceId={mintSpace._id}
                  title={p.name || p.headline}
                  apps={productFolderApps(mintSpace, p, catalog)}
                  buttonClassName="inline-flex h-button-s items-center gap-space-4 rounded-radius-md bg-fill-secondary px-space-12 text-caps-button-s uppercase text-label-primary"
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <Alert
        open={Boolean(removing)}
        title={t("mintSpaceSettings.productsDeleteConfirm")}
        message={removing?.name || removing?.headline || t("mintSpaceSettings.productsUntitled")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        loading={deleting === removing?._id}
        onConfirm={async () => {
          await handleDelete(removing._id);
          setRemoving(null);
        }}
        onClose={() => setRemoving(null)}
      />
    </div>
  );
};

const PaymentsPanel = ({ t, balance, onDeposit, onWithdraw, navigate }) => (
  <div className={PANEL}>
    <SectionHeader
      title={t("mintSpaceSettings.paymentsTitle")}
      subtitle={t("mintSpaceSettings.paymentsSubtitle")}
    />
    <BalanceCard
      t={t}
      label={t("mintSpaceSettings.homeTotalBalance")}
      balance={balance}
      onDeposit={onDeposit}
      onWithdraw={onWithdraw}
    />
    <PersonalWalletNote t={t} navigate={navigate} />
  </div>
);

// =============================================================================
// Sales — every purchase: who bought what, when, for how much
// =============================================================================

const SALE_STATUS_FILTERS = ["", "paid", "refunded", "trial"];

const SALE_STATUS_TONES = {
  paid: "success",
  partially_refunded: "warning",
  refunded: "danger",
};

function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * The space's sales, a page at a time, filtered by product, status and buyer. `focus`
 * comes from a sale notification: open on that sale and mark it, or on that buyer's
 * purchases.
 */
const SalesPanel = ({ t, mintSpaceId, focus }) => {
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [buyer, setBuyer] = useState(focus?.buyer || "");
  const [page, setPage] = useState(1);
  /** A sale to open the list on, once: the server picks the page that holds it. */
  const [locate, setLocate] = useState(focus?.purchase || null);
  const [highlight, setHighlight] = useState(focus?.purchase || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const rowRefs = useRef({});

  // Another sale notification opened while the list is showing
  useEffect(() => {
    if (!focus) return;
    setProduct("");
    setStatus("");
    setQuery("");
    setSearch("");
    setBuyer(focus.buyer || "");
    setPage(1);
    setLocate(focus.purchase || null);
    setHighlight(focus.purchase || null);
  }, [focus]);

  // Search as the name is typed, without a request per keystroke
  useEffect(() => {
    const next = query.trim();
    if (next === search) return;
    const id = setTimeout(() => {
      setSearch(next);
      setPage(1);
      setLocate(null);
    }, 300);
    return () => clearTimeout(id);
  }, [query, search]);

  useEffect(() => {
    if (!mintSpaceId) return;
    let alive = true;
    setLoading(true);
    getMintSpaceSales(mintSpaceId, { page, product, status, q: search, buyer, purchase: locate })
      .then((res) => {
        if (!alive) return;
        setData(res?.data || null);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [mintSpaceId, page, product, status, search, buyer, locate]);

  // Bring the sale a notification pointed at into view
  useEffect(() => {
    const row = data?.focus && rowRefs.current[data.focus];
    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data]);

  const refilter = (apply) => {
    apply();
    setPage(1);
    setLocate(null);
  };
  const goToPage = (next) => {
    setPage(next);
    setLocate(null);
  };

  const filtered = !!(product || status || search || buyer);
  const sales = data?.sales || [];

  return (
    <div className="flex flex-col gap-space-24">
      <SectionHeader title={t("mintSpaceSettings.salesTitle")} subtitle={t("mintSpaceSettings.salesSubtitle")} />

      <div className="flex items-center gap-space-8">
        <SearchField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("mintSpaceSettings.salesSearch")}
          className="min-w-0 flex-1"
        />
        <PopUpButton
          label={t("mintSpaceSettings.salesProduct")}
          value={product}
          placeholder={t("mintSpaceSettings.salesAllProducts")}
          onChange={(value) => refilter(() => setProduct(value))}
          options={(data?.products || []).map((p) => ({ value: p._id, label: p.name }))}
          className="max-w-[50%] shrink-0"
        />
      </div>

      <SegmentedControl
        track="material"
        label={t("mintSpaceSettings.salesTitle")}
        value={status}
        onChange={(key) => refilter(() => setStatus(key))}
        className="max-[599px]:[&>button]:flex-auto max-[599px]:[&>button]:px-space-8"
        options={SALE_STATUS_FILTERS.map((key) => ({
          value: key,
          label: t(`mintSpaceSettings.salesFilter_${key || "all"}`),
        }))}
      />

      {buyer && (
        <div className="inline-flex items-center gap-space-8 self-start rounded-full bg-fill-secondary py-space-4 pl-space-12 pr-space-4 text-footnote text-label-primary">
          <span>{t("mintSpaceSettings.salesBuyerFilter")}</span>
          <UserProfileLink user={data?.buyer} showAvatar={false} fallback={userNameOf(data?.buyer)} nameClassName="font-semibold" />
          <IconButton label={t("mintSpaceSettings.salesClearBuyer")} variant="plain" onClick={() => refilter(() => setBuyer(""))}>
            <Icon name="close" size={16} />
          </IconButton>
        </div>
      )}

      <div className={`flex flex-col gap-space-8 ${CARD} p-space-16`}>
        <div className="flex items-center justify-between gap-space-8">
          <h3 className="text-headline text-label-primary">
            {data ? t("mintSpaceSettings.salesCount", { count: data.total }) : t("mintSpaceSettings.salesTitle")}
          </h3>
          {loading && <FiLoader className="h-4 w-4 animate-spin text-label-secondary" />}
        </div>

        {failed && !data ? (
          <p className="text-footnote text-status-danger">{t("mintSpaceSettings.salesLoadError")}</p>
        ) : !data ? null : sales.length === 0 ? (
          <p className="text-footnote text-label-tertiary">
            {filtered ? t("mintSpaceSettings.salesNoMatch") : t("mintSpaceSettings.salesEmpty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-space-12">
            {sales.map((sale) => {
              const marked = String(sale._id) === String(highlight);
              return (
                <ListRow
                  key={sale._id}
                  rowRef={(el) => {
                    if (el) rowRefs.current[sale._id] = el;
                  }}
                  className={marked ? "-mx-space-8 rounded-radius-sm bg-accent-tinted p-space-8 shadow-[inset_0_0_0_1px_var(--color-accent-default)]" : ""}
                  avatar={getProfileAvatarUrl(sale.buyer)}
                  name={userNameOf(sale.buyer)}
                  title={sale.product}
                  subtitle={
                    <>
                      <BuyerLink user={sale.buyer} /> · {formatDateTime(sale.paidAt)}
                      {sale.promoCode && ` · ${t("mintSpaceSettings.salesPromo", { code: sale.promoCode })}`}
                    </>
                  }
                  value={
                    sale.status === "trial"
                      ? t("mintSpaceSettings.salesTrialDays", { days: sale.trialDays })
                      : formatMoney(sale.amount)
                  }
                  note={
                    <span className="flex items-center gap-space-4">
                      {sale.refundedAmount > 0 && sale.status === "partially_refunded" && (
                        <span className="text-status-danger">−{formatMoney(sale.refundedAmount)}</span>
                      )}
                      <Badge size="s" tone={SALE_STATUS_TONES[sale.status] || "neutral"}>
                        {t(`mintSpaceSettings.purchaseStatus_${sale.status}`)}
                      </Badge>
                    </span>
                  }
                />
              );
            })}
          </ul>
        )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between gap-space-8 border-t border-separator-default pt-space-12 text-footnote">
            <Button
              variant="gray"
              size="s"
              icon={<Icon name="chevron-left" size={16} />}
              disabled={loading || data.page <= 1}
              onClick={() => goToPage(data.page - 1)}
            >
              {t("mintSpaceSettings.salesPrev")}
            </Button>
            <span className="text-label-secondary">{t("mintSpaceSettings.salesPage", { page: data.page, pages: data.pages })}</span>
            <Button
              variant="gray"
              size="s"
              icon={<Icon name="chevron-right" size={16} />}
              disabled={loading || data.page >= data.pages}
              onClick={() => goToPage(data.page + 1)}
            >
              {t("mintSpaceSettings.salesNext")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Team
// =============================================================================

const TEAM_ROLE_SLOTS = ["owner", "admin", "moderator", "support", "analyst"];

const TeamPanel = ({ t, mintSpace, activity, loading, setToast, onRefresh }) => {
  const owner = mintSpace?.owner;
  const ownerId = (owner?._id || owner?.id || owner)?.toString?.() || "";
  const teamRoles = mintSpace?.teamRoles || [];
  const [members, setMembers] = useState([]);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    if (!mintSpace?._id) return;
    getMintSpaceMembers(mintSpace._id)
      .then((res) => setMembers(res?.data?.members || res?.data || []))
      .catch(() => setMembers([]));
  }, [mintSpace?._id]);

  const memberForRole = (role) => {
    if (role === "owner") return owner;
    const entry = teamRoles.find((tr) => tr.role === role);
    return entry?.userId || entry?.user || null;
  };

  const assignableMembers = members.filter((m) => {
    const id = (m._id || m.id)?.toString?.();
    return id && id !== ownerId;
  });

  const handleAssign = async (role, userId) => {
    if (!mintSpace?._id || !userId || role === "owner") return;
    setAssigning(role);
    try {
      const res = await assignTeamRole(mintSpace._id, userId, role);
      if (res?.success !== false) {
        setToast(t("mintSpaceSettings.teamRoleAssigned") || "Role updated");
        await onRefresh?.();
      } else {
        setToast(res?.message || t("toasts.error"));
      }
    } catch (err) {
      setToast(err?.message || t("toasts.error"));
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className={PANEL}>
      <SectionHeader
        title={t("mintSpaceSettings.teamTitle")}
        subtitle={t("mintSpaceSettings.teamSubtitleExpanded")}
      />
      <div className="grid grid-cols-1 items-start gap-space-16 min-[600px]:grid-cols-3 min-[600px]:gap-space-20 lg:gap-space-24">
        {TEAM_ROLE_SLOTS.map((role) => {
          const user = memberForRole(role);
          const name = user ? userNameOf(user) : null;
          return (
            <div key={role} className={`flex min-w-0 flex-col gap-space-8 ${CARD} p-space-16`}>
              <p className="truncate text-caps-label uppercase text-label-secondary">{t(`mintSpaceSettings.teamRole_${role}`)}</p>
              {user ? (
                <div className="flex min-w-0 items-center gap-space-8">
                  <Avatar size="m" src={getProfileAvatarUrl(user)} name={name} initials={name[0]?.toUpperCase()} />
                  <UserProfileLink
                    user={user}
                    showAvatar={false}
                    nameClassName="text-subheadline-semibold text-label-primary"
                    fallback={name}
                  />
                </div>
              ) : (
                <p className="text-footnote text-label-tertiary">{t("mintSpaceSettings.teamRoleEmpty")}</p>
              )}
              {role !== "owner" && (
                <PopUpButton
                  label={t(`mintSpaceSettings.teamRole_${role}`)}
                  value=""
                  placeholder={t("mintSpaceSettings.teamAssignMember") || "Assign member…"}
                  disabled={!!assigning || assignableMembers.length === 0}
                  onChange={(id) => id && handleAssign(role, id)}
                  options={assignableMembers.map((m) => ({
                    value: m._id || m.id,
                    label: m.username || m.businessName || m.email,
                  }))}
                  className="w-full"
                />
              )}
            </div>
          );
        })}
      </div>

      <div className={`flex flex-col ${CARD} p-space-16`}>
        <h3 className="text-headline text-label-primary">{t("mintSpaceSettings.teamActivityHeading")}</h3>
        {loading ? (
          <p className="pt-space-8 text-footnote text-label-tertiary">{t("common.loading") || "Loading..."}</p>
        ) : !activity.length ? (
          <p className="pt-space-8 text-footnote text-label-tertiary">{t("mintSpaceSettings.teamEmpty")}</p>
        ) : (
          <ol>
            {activity.map((row) => {
              const actor = row.actor || {};
              const name = userNameOf(actor);
              return (
                <li key={row._id} className="flex items-center gap-space-12 py-space-12">
                  <Avatar size="m" src={getProfileAvatarUrl(actor)} name={name} initials={name[0]?.toUpperCase()} />
                  <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                    <p className="text-subheadline text-label-primary">
                      <UserProfileLink user={actor} showAvatar={false} fallback={name} /> · {activityLabel(row, t)}
                    </p>
                    <p className="text-footnote text-label-tertiary">{new Date(row.createdAt).toLocaleString()}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Danger zone — deleting the space
// =============================================================================

/** Refusals the server names by code, in the reader's language. */
const DELETE_ERROR_KEYS = {
  confirmation_mismatch: "mintSpaceSettings.deleteNameMismatch",
  keepz_unavailable: "mintSpaceSettings.deleteKeepzUnavailable",
  charge_in_progress: "mintSpaceSettings.deleteChargeInProgress",
};

const DangerZonePanel = ({ t, isDark, mintSpace, navigate, setToast, walletAmount, onMoveMoney }) => {
  const [check, setCheck] = useState(null); // { canDelete, blockers, impact }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const name = mintSpace?.name || "";
  const nameMatches = typed.trim().toLowerCase() === name.trim().toLowerCase();

  const load = useCallback(async () => {
    if (!mintSpace?._id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getMintSpaceDeletionCheck(mintSpace._id);
      setCheck(res?.data || null);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [mintSpace?._id]);

  // Checked again once money leaves the wallet, so that blocker clears by itself.
  useEffect(() => {
    load();
  }, [load, walletAmount]);

  const blockerText = (b) =>
    b.code === "wallet_balance"
      ? t("mintSpaceSettings.deleteBlocker_wallet_balance", { amount: formatMoney(b.amount) })
      : t(`mintSpaceSettings.deleteBlocker_${b.code}`);

  const blockerAction = (b) => {
    if (b.code === "wallet_balance") {
      return (
        <Button variant="gray" size="s" onClick={onMoveMoney}>
          {t("mintSpaceSettings.deleteMoveMoney")}
        </Button>
      );
    }
    if (b.code === "pending_refund_requests") {
      return (
        <Button variant="gray" size="s" onClick={() => navigate(`/mint-space/${mintSpace._id}?nav=refunds`)}>
          {t("mintSpaceSettings.deleteOpenRefunds")}
        </Button>
      );
    }
    return null;
  };

  const remove = async () => {
    if (!nameMatches || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await deleteMintSpace(mintSpace._id, typed.trim());
      setToast(t("mintSpaceSettings.deleted"));
      window.dispatchEvent(
        new CustomEvent("mintSpaceDeleted", { detail: { mintSpaceId: String(mintSpace._id) } })
      );
      navigate("/create-business", { replace: true });
    } catch (err) {
      if (err?.code === "blocked" && Array.isArray(err.blockers)) {
        // Something changed since the check — show what's in the way now.
        setCheck((prev) => ({ ...(prev || {}), canDelete: false, blockers: err.blockers }));
        setConfirming(false);
      } else {
        setError(t(DELETE_ERROR_KEYS[err?.code] || "mintSpaceSettings.deleteFailed"));
      }
    } finally {
      setDeleting(false);
    }
  };

  const impact = check && !check.blockers?.length ? check.impact : null;

  return (
    <div className={PANEL}>
      <SectionHeader title={t("mintSpaceSettings.dangerTitle")} subtitle={t("mintSpaceSettings.dangerSubtitle")} />
      <div className="flex flex-col gap-space-12 rounded-radius-lg bg-status-danger-bg p-space-16">
        <h3 className="text-headline text-label-primary">{t("mintSpaceSettings.deleteSpaceTitle")}</h3>
        <p className="text-footnote text-label-secondary">{t("mintSpaceSettings.deleteSpaceDescription", { name })}</p>
        {loading && !check && <p className="text-footnote text-label-secondary">{t("mintSpaceSettings.deleteChecking")}</p>}
        {impact && (
          <ul className="flex list-disc flex-col gap-space-4 pl-space-20 text-footnote text-label-secondary">
            <li>{t("mintSpaceSettings.deleteImpactMembers", { count: impact.members ?? 0 })}</li>
            {impact.subscriptions > 0 && (
              <li>{t("mintSpaceSettings.deleteImpactSubscriptions", { count: impact.subscriptions })}</li>
            )}
            <li>{t("mintSpaceSettings.deleteKept")}</li>
          </ul>
        )}
        <Button
          variant="gray"
          size="m"
          icon={<Icon name="trash" size={20} />}
          className={`self-start ${check?.canDelete ? "!text-status-danger" : ""}`}
          disabled={!check?.canDelete}
          onClick={() => {
            setTyped("");
            setError("");
            setConfirming(true);
          }}
        >
          {t("mintSpaceSettings.deleteSpaceButton")}
        </Button>
      </div>

      {loadError ? (
        <InlineMessage tone="danger" title={t("mintSpaceSettings.deleteCheckFailed")}>
          <button type="button" onClick={load} className="text-accent-text hover:underline">
            {t("payment.retry")}
          </button>
        </InlineMessage>
      ) : check?.blockers?.length ? (
        <InlineMessage tone="warning" icon="clock" title={t("mintSpaceSettings.deleteBlockedIntro")}>
          <span className="flex flex-col gap-space-8">
            {check.blockers.map((b) => (
              <span key={b.code} className="flex flex-wrap items-center justify-between gap-space-8">
                <span className="min-w-0 flex-1">{blockerText(b)}</span>
                {blockerAction(b)}
              </span>
            ))}
          </span>
        </InlineMessage>
      ) : null}

      <Modal
        open={confirming}
        onClose={() => !deleting && setConfirming(false)}
        title={t("mintSpaceSettings.deleteModalTitle", { name })}
        description={t("mintSpaceSettings.deleteSpaceDescription", { name })}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <TextField
          autoComplete="off"
          label={t("mintSpaceSettings.deleteConfirmLabel", { name })}
          error={error || undefined}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") remove();
          }}
          placeholder={name}
        />
        <div className="flex justify-end gap-space-8">
          <Button variant="gray" size="m" disabled={deleting} onClick={() => setConfirming(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" size="m" loading={deleting} disabled={!nameMatches} onClick={remove}>
            {deleting ? t("mintSpaceSettings.deleting") : t("mintSpaceSettings.deleteConfirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

// =============================================================================
// Balance transfer modal (business ↔ personal)
// =============================================================================

const BalanceTransferModal = ({ t, isDark, mode, businessBalance, personalBalance, submitting, onClose, onSubmit }) => {
  const [amount, setAmount] = useState("");
  const max =
    mode === "deposit"
      ? Number(personalBalance?.amount) || 0
      : Number(businessBalance?.amount) || 0;

  return (
    <Modal
      open
      onClose={() => !submitting && onClose()}
      title={t(mode === "deposit" ? "mintSpaceSettings.transferDepositTitle" : "mintSpaceSettings.transferWithdrawTitle")}
      description={t(mode === "deposit" ? "mintSpaceSettings.transferDepositHelp" : "mintSpaceSettings.transferWithdrawHelp")}
      closeLabel={t("common.close")}
      isDark={isDark}
    >
      <TextField
        type="number"
        min="0"
        step="0.01"
        max={max}
        label={t("mintSpaceSettings.transferAmount")}
        hint={`${t("mintSpaceSettings.transferAvailable")}: ${formatMoney(max)}`}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
      />
      <div className="flex justify-end gap-space-8">
        <Button variant="gray" disabled={submitting} onClick={onClose}>
          {t("common.cancel") || "Cancel"}
        </Button>
        <Button
          loading={submitting}
          disabled={!amount || Number(amount) <= 0 || Number(amount) > max}
          onClick={() => onSubmit(Number(amount))}
        >
          {submitting ? t("mintSpaceSettings.transferring") : t("common.confirm") || "Confirm"}
        </Button>
      </div>
    </Modal>
  );
};

// =============================================================================
// Form defaults
// =============================================================================

/**
 * Turn a stored policy sub-doc into the shape the editor expects. Falls back to legacy
 * flat fields when the nested doc hasn't been written yet, choosing a sensible mode based
 * on what's populated.
 */
function normalizePolicyDoc(nested, legacy = {}) {
  if (nested && (nested.mode || nested.html || nested.url || nested.fileUrl)) {
    return {
      mode: nested.mode || (nested.html ? "text" : nested.fileUrl ? "file" : "url"),
      html: nested.html || "",
      url: nested.url || legacy.url || "",
      fileUrl: nested.fileUrl || legacy.fileUrl || "",
      fileName: nested.fileName || "",
    };
  }
  const legacyUrl = legacy.url || "";
  const legacyFile = legacy.fileUrl || "";
  return {
    mode: legacyFile ? "file" : "url",
    html: "",
    url: legacyUrl,
    fileUrl: legacyFile,
    fileName: "",
  };
}

function defaultFormFromSpace(mintSpace) {
  return {
    business: {
      name: mintSpace?.business?.name || "",
      supportEmail: mintSpace?.business?.supportEmail || "",
      streetAddress: mintSpace?.business?.streetAddress || "",
      apt: mintSpace?.business?.apt || "",
      city: mintSpace?.business?.city || "",
      zipCode: mintSpace?.business?.zipCode || "",
      country: mintSpace?.business?.country || "GE",
    },
    tax: {
      type: mintSpace?.tax?.type || "exclusive",
      vatId: mintSpace?.tax?.vatId || "",
    },
    policies: {
      terms: normalizePolicyDoc(mintSpace?.policies?.terms, {
        url: mintSpace?.policies?.termsUrl,
        fileUrl: mintSpace?.policies?.termsFileUrl,
      }),
      privacy: normalizePolicyDoc(mintSpace?.policies?.privacy, {
        url: mintSpace?.policies?.privacyUrl,
        fileUrl: mintSpace?.policies?.privacyFileUrl,
      }),
      termsUrl: mintSpace?.policies?.termsUrl || "",
      privacyUrl: mintSpace?.policies?.privacyUrl || "",
      termsFileUrl: mintSpace?.policies?.termsFileUrl || "",
      privacyFileUrl: mintSpace?.policies?.privacyFileUrl || "",
    },
    options: {
      requireTermsAcceptance: !!mintSpace?.options?.requireTermsAcceptance,
    },
    features: {
      payments: mintSpace?.features?.payments !== false,
      supportChat:
        mintSpace?.features?.supportChat ?? !!mintSpace?.pages?.support?.enabled,
      withdrawals: mintSpace?.features?.withdrawals !== false,
    },
    notificationPrefs: {
      email: {
        newSubmission: mintSpace?.notificationPrefs?.email?.newSubmission !== false,
        appeal: mintSpace?.notificationPrefs?.email?.appeal !== false,
        payout: mintSpace?.notificationPrefs?.email?.payout !== false,
        supportMessage: mintSpace?.notificationPrefs?.email?.supportMessage !== false,
      },
      inApp: {
        newSubmission: mintSpace?.notificationPrefs?.inApp?.newSubmission !== false,
        appeal: mintSpace?.notificationPrefs?.inApp?.appeal !== false,
        payout: mintSpace?.notificationPrefs?.inApp?.payout !== false,
        supportMessage: mintSpace?.notificationPrefs?.inApp?.supportMessage !== false,
      },
    },
  };
}

export default MintSpaceSettingsPage;
